/**
 * api.ts — central frontend API client.
 *
 * Owns: every typed wrapper around backend HTTP endpoints, grouped by
 *       module (metricsApi, projectsApi, processesApi, salesApi,
 *       inboxApi, eosApi, communicationsApi, integrationsApi, etc.).
 *       Each function is a thin wrapper that calls the shared axios
 *       instance from `apiClient.ts` and normalises the response.
 * Used by: every page, view, dialog, and dashboard in the frontend that
 *       talks to the backend. Imports through this file are the canonical
 *       access path — direct fetch / axios calls in other components are
 *       a smell.
 * Depends on: `./apiClient` for the shared axios instance + base URL,
 *       `../types` for response shapes.
 * Notes: Frozen file (operating-rules.md §4). At 3,787 LOC, broad edits
 *       here can break every module at once. Reads
 *       `process.env.REACT_APP_API_URL` directly for the file-download
 *       helper that needs an absolute origin (the rest of the file uses
 *       `apiClient`'s baseURL). Ticket 0b-02 (error-shape unification)
 *       and 0b-05 (`useApiMutation` shared hook) will be the next major
 *       passes through this file.
 */
import { api, API_BASE_URL } from './apiClient';
import {
  Metric,
  MetricWithValue,
  MetricValue,
  Department,
  MetricTarget,
  MetricVisibilityRecord,
  ScorecardMetric,
  QuarterInfo,
  Dashboard,
  DashboardWidget,
  WidgetType,
  WidgetSize,
  OperationsClient,
  ActionPlan,
  ActionPlanWeek,
  ClientTimeSummary,
  TimeEntryRow,
  TodoItem,
  TodoStatus,
  TodoSavedView,
  TodoActivityEntry,
  Project,
  ProjectActivityEntry,
  ClientDomain,
  ClientWithTickets,
  ScrapedLead,
  ScrapedEvent,
  ScrapeJob,
  SalesHubStats,
  LeadFollowUp,
  SalesDashboardStats,
  Pipeline,
  PipelineStage,
  LeadHistoryEntry,
  PipelineSavedView,
  SalesCampaign,
} from '../types';
import type {
  ProcessTemplate, ProcessStep, ProcessConnection, ProcessCustomField,
  ProcessRole, ProcessRecurringSchedule, ProcessInstance, ProcessTaskInstance,
  ProcessActivityEntry,
  FlowchartProject, FlowchartFile, FlowchartDocument, FlowchartNode, FlowchartEdge, FlowchartAIHistory,
} from '../types';

export { API_BASE_URL } from './apiClient';
export { dashboardLayoutApi } from './api/dashboardLayoutApi';
export type { DashboardLayoutWidget, UserDashboard } from './api/dashboardLayoutApi';
export { searchApi } from './api/searchApi';
export type { GlobalSearchResults } from './api/searchApi';

export const metricsApi = {
  getAll: (filters?: {
    departmentId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<MetricWithValue[]> => {
    return api.get('/metrics', { params: filters }).then(res => res.data.data);
  },

  getById: (id: number): Promise<Metric> => {
    return api.get(`/metrics/${id}`).then(res => res.data.data);
  },

  getAnnotations: (id: number): Promise<Array<{ id: number; weekStart: string; note: string }>> => {
    return api.get(`/metrics/${id}/annotations`).then(res => res.data.data);
  },

  createAnnotation: (metricId: number, data: { week_start: string; note: string }): Promise<{ id: number }> => {
    return api.post(`/metrics/${metricId}/annotations`, data).then(res => res.data);
  },

  deleteAnnotation: (id: number): Promise<void> => {
    return api.delete(`/metrics/annotations/${id}`).then(() => undefined);
  },

  create: (metric: Omit<Metric, 'id' | 'created_at'> & { clockifyFilters?: any[] }): Promise<Metric & { clockifyFilters?: any[] }> => {
    return api.post('/metrics', metric).then(res => res.data);
  },

  update: (id: number, metric: Omit<Metric, 'id' | 'created_at'> & { clockifyFilters?: any[] }): Promise<Metric & { clockifyFilters?: any[] }> => {
    return api.put(`/metrics/${id}`, metric).then(res => res.data);
  },

  addValue: (id: number, value: number, date: string): Promise<MetricValue> => {
    return api.post(`/metrics/${id}/values`, { value, date }).then(res => res.data);
  },

  refresh: (): Promise<{ message: string; timestamp: string }> => {
    return api.post('/metrics/refresh').then(res => res.data.data);
  },

  /** Starts background sync; returns jobId for SSE progress. Final result comes via progress events. */
  delete: (id: number): Promise<{ message: string }> => {
    return api.delete(`/metrics/${id}`).then(res => res.data.data);
  },

  restore: (id: number): Promise<Metric> => {
    return api.post(`/metrics/${id}/restore`).then(res => res.data.data);
  },
};

export const departmentsApi = {
  getAll: (): Promise<Department[]> => {
    return api.get('/departments').then(res => res.data.data);
  },

  create: (name: string): Promise<Department> => {
    return api.post('/departments', { name }).then(res => res.data.data);
  },
};

export const usersApi = {
  getRoles: (): Promise<Array<{ id: number; name: string }>> =>
    api.get('/users/roles/all').then(r => r.data.data),
  getSignature: (): Promise<{ success: boolean; data: { user_id: number; signature_html: string; is_enabled: number } }> =>
    api.get('/users/me/signature').then(r => r.data),
  updateSignature: (data: { signature_html: string; is_enabled?: number }): Promise<{ success: boolean; data: any }> =>
    api.put('/users/me/signature', data).then(r => r.data),

  getMyTickets: (params?: { startDate?: string; endDate?: string; status?: string }): Promise<{
    unmapped?: boolean;
    items?: Array<{
      id: number;
      metric_id: number;
      metric_name: string;
      metric_unit: string | null;
      department_name: string | null;
      week_date: string;
      source_id: string;
      title: string;
      url: string | null;
      assignee: string | null;
      status: 'positive' | 'negative' | 'neutral';
      details: Record<string, any> | null;
    }>;
    assignee?: string;
  }> => api.get('/users/me/tickets', { params }).then(res => res.data.data),
};

export const targetsApi = {
  set: (
    metricId: number,
    targetValue: number,
    quarter: number,
    year: number,
    options?: {
      target_type?: 'total' | 'threshold';
      threshold_direction?: 'below' | 'above';
      evaluation_mode?: 'weekly' | 'rolling';
    }
  ): Promise<MetricTarget> => {
    return api.post('/targets', {
      metric_id: metricId,
      target_value: targetValue,
      quarter,
      year,
      ...options,
    }).then(res => res.data.data);
  },

  getCurrentQuarter: (): Promise<QuarterInfo> => {
    return api.get('/targets/info/current-quarter').then(res => res.data.data);
  },

  getQuarterInfo: (quarter: number, year: number): Promise<QuarterInfo> => {
    return api.get(`/targets/info/quarter/${quarter}/${year}`).then(res => res.data.data);
  },

  getForQuarter: (quarter: number, year: number): Promise<MetricTarget[]> => {
    return api.get(`/targets/quarter/${quarter}/${year}`).then(res => res.data.data);
  },
};

export const scorecardApi = {
  get: (quarter?: number, year?: number, startDate?: string, endDate?: string, assignee?: string, assignees?: string[]): Promise<ScorecardMetric[]> => {
    const params: any = {};
    if (quarter) params.quarter = quarter;
    if (year) params.year = year;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (assignee) params.assignee = assignee;
    if (assignees && assignees.length > 0) params.assignees = assignees.join(',');
    return api.get('/scorecard', { params }).then(res => res.data.data);
  },
};

export const aptlyApi = {
  getPortfolioClients: (): Promise<{
    success: boolean;
    data?: {
      clients: Array<{ card_id: string; title: string; stage: string | null; is_active: boolean; project_manager: string | null; implementer: string | null; expected_wrap_up: string | null }>;
      activeCount: number;
    };
    error?: string;
  }> => {
    return api.get('/aptly/portfolio/clients').then(res => res.data);
  },

  setClientWrapUpDate: (cardId: string, expected_wrap_up: string | null): Promise<{ success: boolean }> => {
    return api.put(`/aptly/portfolio/clients/${cardId}/wrap-up`, { expected_wrap_up }).then(res => res.data);
  },
};

export const dashboardsApi = {
  list: (filters?: { departmentId?: number | null; includeMixed?: boolean }): Promise<Dashboard[]> => {
    return api.get('/dashboards', { params: filters }).then(res => res.data.data);
  },
  create: (name: string, options?: { department_id?: number | null; is_mixed?: boolean }): Promise<Dashboard> => {
    return api.post('/dashboards', { name, ...options }).then(res => res.data.data);
  },
  update: (dashboardId: number, updates: { name?: string }): Promise<Dashboard> => {
    return api.put(`/dashboards/${dashboardId}`, updates).then(res => res.data.data);
  },
  delete: (dashboardId: number): Promise<{ success: boolean }> => {
    return api.delete(`/dashboards/${dashboardId}`).then(res => res.data.data);
  },
  listWidgets: (dashboardId: number): Promise<DashboardWidget[]> => {
    return api.get(`/dashboards/${dashboardId}/widgets`).then(res => res.data.data);
  },
  createWidget: (dashboardId: number, widget: {
    title: string;
    widget_type: WidgetType;
    size: WidgetSize;
    position: number;
    config: string | null;
  }): Promise<DashboardWidget> => {
    return api.post(`/dashboards/${dashboardId}/widgets`, widget).then(res => res.data.data);
  },
  updateWidget: (widgetId: number, updates: Partial<{
    title: string;
    widget_type: WidgetType;
    size: WidgetSize;
    position: number;
    config: string | null;
  }>): Promise<DashboardWidget> => {
    return api.put(`/dashboards/widgets/${widgetId}`, updates).then(res => res.data.data);
  },
  deleteWidget: (widgetId: number): Promise<{ success: boolean }> => {
    return api.delete(`/dashboards/widgets/${widgetId}`).then(res => res.data.data);
  },
  getMetrics: (dashboardId: number): Promise<{ metricIds: number[] }> => {
    return api.get(`/dashboards/${dashboardId}/metrics`).then(res => res.data.data);
  },
  setMetrics: (dashboardId: number, metricIds: number[]): Promise<{ metricIds: number[] }> => {
    return api.put(`/dashboards/${dashboardId}/metrics`, { metricIds }).then(res => res.data.data);
  },
};

export const metricVisibilityApi = {
  get: (params?: {
    dashboardId?: number | null;
    departmentId?: number | null;
    isMixed?: boolean;
  }): Promise<Array<{ metric_id: number; metric_name: string; hidden: boolean }>> => {
    return api.get('/metric-visibility', { params }).then(res => res.data.data);
  },

  batchUpdate: (visibility: Array<{ metricId: number; hidden: boolean }>, params?: {
    dashboardId?: number | null;
    departmentId?: number | null;
    isMixed?: boolean;
  }): Promise<{ success: boolean; updated: number }> => {
    return api.post('/metric-visibility/batch', {
      visibility,
      ...params,
    }).then(res => res.data);
  },

  cleanup: (): Promise<{ success: boolean; deleted: number }> => {
    return api.post('/metric-visibility/cleanup').then(res => res.data);
  },
};


export type ScorecardViewFilters = {
  departments?: string[];     // e.g., ['Sales', 'Operations']
  assignee?: string;          // filter by person (single, legacy)
  assignees?: string[];       // filter by multiple people
  metricIds?: number[];       // specific metrics to show
};

// ── Forecast (capacity planning; uses time-tracking data) ──

export const forecastApi = {
  getForecast: (): Promise<{
    success: boolean;
    data?: {
      defaultCapacity: number;
      periods: string[];
      teams: Array<{ id: number; name: string; members: Array<{ id: number; member_name: string; role: 'pm' | 'implementer'; allocated_hours: number }> }>;
      teamForecasts: Array<{
        teamId: number;
        teamName: string;
        teamCapacity: number;
        clients: Array<{ clientTitle: string; stage: string | null; budgetOn: number; budgetOff: number; wrapUpDate: string | null }>;
        memberForecasts: Array<{
          name: string;
          role: 'pm' | 'implementer';
          allocatedHours: number;
          periods: Array<{ committed: number; available: number; clientBreakdown: Array<{ client: string; hours: number }> }>;
        }>;
        periods: Array<{ committed: number; available: number }>;
      }>;
      salesReadiness: Array<{ month: string; totalAvailable: number; teams: Array<{ teamName: string; available: number }> }>;
      clientRoster: Array<{ cardId: string; title: string; stage: string | null; pm: string | null; implementer: string | null; budgetOn: number; budgetOff: number; totalBudget: number; wrapUpDate: string | null; projectId: string | null }>;
      unmatchedClients: Array<{ clientTitle: string; stage: string | null; budgetOn: number; budgetOff: number; wrapUpDate: string | null; pm: string | null; implementer: string | null }>;
      hiddenMembers: string[];
    };
    error?: string;
  }> => api.get('/forecast/forecast').then(res => res.data),

};

// ── Finances (P&L Upload) ──

export const financesApi = {
  /** Upload a P&L CSV/Excel file */
  uploadPnl: (file: File, basis?: 'accrual' | 'cash'): Promise<{
    success: boolean;
    data?: {
      importId: number;
      filename: string;
      basis: 'accrual' | 'cash';
      periodLabel: string | null;
      periodStart: string | null;
      periodEnd: string | null;
      totalIncome: number;
      totalCogs: number;
      grossProfit: number;
      totalExpenses: number;
      netIncome: number;
      lineItemCount: number;
      sections: string[];
    };
    error?: string;
    debug?: any;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (basis) formData.append('basis', basis);
    return api.post('/finances/pnl/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(res => res.data);
  },

  /** List all P&L imports */
  getPnlImports: (basis?: 'accrual' | 'cash'): Promise<{
    success: boolean;
    data?: Array<{
      id: number;
      filename: string;
      period_start: string | null;
      period_end: string | null;
      period_label: string | null;
      total_income: number;
      total_cogs: number;
      gross_profit: number;
      total_expenses: number;
      net_income: number;
      basis: 'accrual' | 'cash';
      uploaded_at: string;
    }>;
    error?: string;
  }> => api.get('/finances/pnl/imports', { params: basis ? { basis } : {} }).then(res => res.data),

  /** Get full P&L data (line items) for a specific import or latest */
  getPnlData: (importId?: number, basis?: 'accrual' | 'cash'): Promise<{
    success: boolean;
    data?: {
      import: {
        id: number;
        filename: string;
        period_start: string | null;
        period_end: string | null;
        period_label: string | null;
        total_income: number;
        total_cogs: number;
        gross_profit: number;
        total_expenses: number;
        net_income: number;
        basis: 'accrual' | 'cash';
        uploaded_at: string;
      };
      lineItems: Array<{
        id: number;
        import_id: number;
        section: string;
        category: string;
        subcategory: string | null;
        amount: number;
        depth: number;
        sort_order: number;
      }>;
    } | null;
    error?: string;
  }> => api.get('/finances/pnl/data', { params: { ...(importId ? { importId } : {}), ...(basis ? { basis } : {}) } }).then(res => res.data),

  /** Get all imports for trend comparison */
  deletePnlImport: (id: number): Promise<{ success: boolean; deleted?: boolean; error?: string }> =>
    api.delete(`/finances/pnl/imports/${id}`).then(res => res.data.data),

  // ── Multi-type report endpoints ──

};

// ── Settings (Global Targets & Metrics Management) ──

export interface QuarterlyTargetRow {
  metric_id: number;
  metric_name: string;
  description: string | null;
  unit: string | null;
  metric_type: 'kpi' | 'indicator';
  source_type: 'manual' | 'api' | 'csv';
  department_id: number | null;
  department_name: string | null;
  target_id: number | null;
  target_value: number | null;
  target_type: 'total' | 'threshold' | null;
  threshold_direction: 'below' | 'above' | null;
  evaluation_mode: 'weekly' | 'rolling' | null;
}

export interface TargetHistoryRow {
  id: number;
  metric_id: number;
  metric_name: string;
  unit: string | null;
  target_value: number;
  quarter: number;
  year: number;
  target_type: 'total' | 'threshold';
  threshold_direction: 'below' | 'above';
  evaluation_mode: 'weekly' | 'rolling';
  quarter_actual: number;
  /** How quarter_actual was rolled up (backend: services/scorecardRollup.ts). */
  qtd_mode?: 'sum' | 'avg' | 'latest' | 'quarter';
  hit_target: boolean;
}

/** A row from the W3 support_providers table — backs the dropdown of provider names. */
export interface SupportProvider {
  id: number;
  name: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export const settingsApi = {
  // W Chunk C — per-client CSAT config + skip log. Backs the
  // Settings → Support → CSAT panel.
  csat: {
    list: (): Promise<{ success: boolean; data: any[] }> =>
      api.get('/settings/csat').then(r => r.data),
    upsert: (clientCardId: string, data: { enabled?: boolean; delay_after_resolution_hours?: number; cooldown_between_sends_days?: number; email_template_id?: number | null }): Promise<{ success: boolean; data: any }> =>
      api.patch(`/settings/csat/${encodeURIComponent(clientCardId)}`, data).then(r => r.data),
    skipLog: (opts?: { client_card_id?: string; since_days?: number }): Promise<{ success: boolean; data: any[] }> =>
      api.get('/settings/csat/skip-log', { params: opts || {} }).then(r => r.data),
  },
  /** W3: support_providers CRUD — manages the dropdown values for ticket.support_provider. */
  supportProviders: {
    list: (): Promise<{ success: boolean; data: SupportProvider[] }> =>
      api.get('/settings/support-providers').then(r => r.data),
    create: (name: string): Promise<{ success: boolean; data: SupportProvider; error?: string }> =>
      api.post('/settings/support-providers', { name }).then(r => r.data).catch(err => err.response?.data || { success: false, error: err.message }),
    update: (id: number, data: { name?: string; archived?: boolean }): Promise<{ success: boolean; data: SupportProvider; error?: string }> =>
      api.patch(`/settings/support-providers/${id}`, data).then(r => r.data).catch(err => err.response?.data || { success: false, error: err.message }),
    delete: (id: number): Promise<{ success: boolean; error?: string }> =>
      api.delete(`/settings/support-providers/${id}`).then(r => r.data).catch(err => err.response?.data || { success: false, error: err.message }),
  },

  /** Get all metrics with their targets for a quarter */
  getQuarterlyTargets: (quarter?: number, year?: number): Promise<{
    success: boolean;
    data?: { quarter: number; year: number; metrics: QuarterlyTargetRow[] };
    error?: string;
  }> => {
    const params: Record<string, number> = {};
    if (quarter) params.quarter = quarter;
    if (year) params.year = year;
    return api.get('/settings/quarterly-targets', { params }).then(r => r.data);
  },

  /** Batch save targets for a quarter */
  saveQuarterlyTargets: (quarter: number, year: number, targets: Array<{
    metric_id: number;
    target_value?: number | null;
    target_type?: 'total' | 'threshold';
    threshold_direction?: 'below' | 'above';
    evaluation_mode?: 'weekly' | 'rolling';
    unit?: string | null;
    source_type?: 'manual' | 'api' | 'csv';
    metric_type?: 'kpi' | 'indicator';
  }>): Promise<{ success: boolean; updated?: number; error?: string }> =>
    api.put('/settings/quarterly-targets', { quarter, year, targets }).then(r => ({ success: r.data.success, ...r.data.data })),

  /** Get historical targets for a metric */
  getTargetHistory: (metricId: number): Promise<{
    success: boolean;
    data?: TargetHistoryRow[];
    error?: string;
  }> =>
    api.get('/settings/quarterly-targets/history', { params: { metric_id: metricId } }).then(r => r.data),

  /** Get list of quarters that have target data */
  getAnnualGoals: (year?: number): Promise<{
    success: boolean;
    data?: {
      year: number;
      metrics: Array<{
        metric_id: number;
        metric_name: string;
        description: string | null;
        unit: string | null;
        metric_type: 'kpi' | 'indicator';
        source_type: 'manual' | 'api' | 'csv';
        department_id: number | null;
        department_name: string | null;
        annual_target_id: number | null;
        target_value: number | null;
        target_type: 'total' | 'threshold' | null;
        threshold_direction: 'below' | 'above' | null;
        notes: string | null;
      }>;
    };
    error?: string;
  }> => {
    const params: Record<string, number> = {};
    if (year) params.year = year;
    return api.get('/settings/annual-goals', { params }).then(r => r.data);
  },

  /** Batch save annual goals */
  saveAnnualGoals: (year: number, goals: Array<{
    metric_id: number;
    target_value?: number | null;
    target_type?: 'total' | 'threshold';
    threshold_direction?: 'below' | 'above';
    notes?: string | null;
  }>): Promise<{ success: boolean; updated?: number; removed?: number; error?: string }> =>
    api.put('/settings/annual-goals', { year, goals }).then(r => ({ success: r.data.success, ...r.data.data })),

  /** Get YTD progress for annual goals */
  getAnnualProgress: (year?: number): Promise<{
    success: boolean;
    data?: {
      year: number;
      yearProgress: number;
      progress: Array<{
        id: number;
        metric_id: number;
        metric_name: string;
        unit: string | null;
        department_name: string | null;
        target_value: number;
        target_type: 'total' | 'threshold';
        threshold_direction: 'below' | 'above';
        notes: string | null;
        ytd_actual: number;
        ytd_average: number;
        year_progress: number;
        expected_at_pace: number;
        on_track: boolean;
      }>;
    };
    error?: string;
  }> => {
    const params: Record<string, number> = {};
    if (year) params.year = year;
    return api.get('/settings/annual-goals/progress', { params }).then(r => r.data);
  },

  /** Create a new metric from settings */
  createMetric: (data: {
    name: string;
    department_id?: number | null;
    unit?: string | null;
    source_type?: 'manual' | 'api' | 'csv';
    metric_type?: 'kpi' | 'indicator';
    target_value?: number;
    quarter?: number;
    year?: number;
    target_type?: 'total' | 'threshold';
    threshold_direction?: 'below' | 'above';
    evaluation_mode?: 'weekly' | 'rolling';
  }): Promise<{ success: boolean; data?: any; error?: string }> =>
    api.post('/settings/metrics', data).then(r => r.data),

  // ── Scorecard Saved Views ──

  getScorecardViews: (): Promise<{
    success: boolean;
    data?: Array<{ id: number; name: string; filters: ScorecardViewFilters; position: number; created_at: string }>;
    error?: string;
  }> => api.get('/settings/scorecard-views').then(r => r.data),

  createScorecardView: (name: string, filters: ScorecardViewFilters): Promise<{
    success: boolean;
    data?: { id: number; name: string; filters: ScorecardViewFilters };
    error?: string;
  }> => api.post('/settings/scorecard-views', { name, filters }).then(r => r.data),

  updateScorecardView: (id: number, name: string, filters: ScorecardViewFilters): Promise<{
    success: boolean;
    data?: { id: number; name: string; filters: ScorecardViewFilters };
    error?: string;
  }> => api.put(`/settings/scorecard-views/${id}`, { name, filters }).then(r => r.data),

  deleteScorecardView: (id: number): Promise<{ success: boolean; error?: string }> =>
    api.delete(`/settings/scorecard-views/${id}`).then(r => r.data),

  // ── Person Aliases ──

  getPeople: (): Promise<{ success: boolean; data?: string[]; error?: string }> =>
    api.get('/settings/people').then(r => r.data),

  getIntegrationStatus: (): Promise<{
    success: boolean;
    data?: Array<{
      name: string;
      key: string;
      connected: boolean;
      lastSync: string | null;
    }>;
    error?: string;
  }> => api.get('/settings/integration-status').then(r => r.data),

};

export const widgetDataApi = {
  aggregate: (params: {
    metricId: number;
    dateStart?: string;
    dateEnd?: string;
    bucketBy?: 'days' | 'weeks' | 'months' | 'years';
    calculation?: 'total' | 'average';
    comparisonPeriod?: 'previous_period' | 'previous_year' | 'none';
  }): Promise<{
    success: boolean;
    data?: {
      currentValue: number;
      previousValue: number | null;
      changePercent: number | null;
      changeAbsolute: number | null;
      trend: 'up' | 'down' | 'flat';
      periodLabel: string;
      previousPeriodLabel: string | null;
      timeSeries: Array<{ date: string; value: number; label: string }>;
      breakdown: Array<{ label: string; value: number }>;
      metricUnit: string | null;
      metricName: string | null;
      isRateMetric: boolean;
    };
    error?: string;
  }> => api.get('/widget-data/aggregate', { params }).then(r => r.data),

};

// ── Time Tracking ──

export interface TimeProject {
  id: number;
  name: string;
  aptly_card_id: string | null;
  is_internal: boolean;
  is_active: boolean;
  sort_order: number;
  stage?: string | null;
  project_manager?: string | null;
  implementer?: string | null;
  owner_id?: number | null;
  category_id?: number | null;
}

export interface TimeTask {
  id: number;
  name: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  scope: 'all' | 'client' | 'internal' | 'admin';
  owner_id?: number | null;
}

export interface TaskCategory {
  id: number;
  name: string;
  is_system: boolean;
  sort_order: number;
  default_task_ids: number[];
}

export interface TimeEntry {
  id: number;
  user_id: number;
  project_id: number;
  task_id: number;
  start_time: string;
  end_time: string | null;
  duration_seconds: number | null;
  is_manual: boolean;
  notes: string | null;
  source: string;
  created_at: string;
  updated_at: string;
  project_name?: string;
  task_name?: string;
  user_name?: string;
  user_email?: string;
  needs_review?: boolean;
}

export interface ActiveTimer {
  entry_id: number;
  user_id: number;
  project_id: number;
  task_id: number;
  project_name: string;
  task_name: string;
  start_time: string;
  is_internal: boolean;
}

export interface ProjectSummary {
  project_id: number;
  project_name: string;
  is_internal: boolean;
  is_active: boolean;
  stage: string | null;
  project_manager: string | null;
  implementer: string | null;
  owner_id: number | null;
  tasks: Array<{
    task_id: number;
    task_name: string;
    total_seconds: number;
    total_hours: number;
  }>;
  total_seconds: number;
  total_hours: number;
}

export interface TimesheetRow {
  date: string;
  project_id: number;
  project_name: string;
  task_id: number;
  task_name: string;
  total_seconds: number;
  total_hours: number;
  entries: Array<{ id: number; start_time: string; end_time: string | null; duration_seconds: number | null; notes: string | null }>;
}

export const timeTrackingApi = {
  getActiveTimer: (): Promise<{ success: boolean; data: ActiveTimer | null }> =>
    api.get('/time-tracking/active').then(r => r.data),

  startTimer: (projectId: number, taskId: number): Promise<{ success: boolean; data: { entry: TimeEntry; stoppedEntry?: TimeEntry } }> =>
    api.post('/time-tracking/start', { projectId, taskId }).then(r => r.data),

  stopTimer: (): Promise<{ success: boolean; data: TimeEntry }> =>
    api.post('/time-tracking/stop').then(r => r.data),

  switchTask: (projectId: number, taskId: number): Promise<{ success: boolean; data: { newEntry: TimeEntry; stoppedEntry?: TimeEntry } }> =>
    api.post('/time-tracking/switch', { projectId, taskId }).then(r => r.data),

  getEntries: (params?: {
    userId?: number; userIds?: number[]; projectId?: number; taskId?: number;
    startDate?: string; endDate?: string; source?: string; limit?: number; offset?: number;
    sortBy?: string; sortDir?: 'asc' | 'desc';
  }): Promise<{ success: boolean; data: TimeEntry[]; total: number }> => {
    const query: Record<string, string> = {};
    if (params?.userId) query.userId = String(params.userId);
    if (params?.userIds?.length) query.userIds = params.userIds.join(',');
    if (params?.projectId) query.projectId = String(params.projectId);
    if (params?.taskId) query.taskId = String(params.taskId);
    if (params?.startDate) query.startDate = params.startDate;
    if (params?.endDate) query.endDate = params.endDate;
    if (params?.source) query.source = params.source;
    if (params?.limit) query.limit = String(params.limit);
    if (params?.offset) query.offset = String(params.offset);
    if (params?.sortBy) query.sortBy = params.sortBy;
    if (params?.sortDir) query.sortDir = params.sortDir;
    return api.get('/time-tracking/entries', { params: query }).then(r => r.data);
  },

  createManualEntry: (data: {
    projectId: number; taskId: number; startTime: string; endTime: string; notes?: string; userId?: number;
  }): Promise<{ success: boolean; data: TimeEntry }> =>
    api.post('/time-tracking/entries', data).then(r => r.data),

  updateEntry: (id: number, updates: {
    project_id?: number; task_id?: number; start_time?: string; end_time?: string; notes?: string;
  }): Promise<{ success: boolean; data: TimeEntry }> =>
    api.put(`/time-tracking/entries/${id}`, updates).then(r => r.data),

  deleteEntry: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/time-tracking/entries/${id}`).then(r => r.data),

  getProjects: (activeOnly?: boolean): Promise<{ success: boolean; data: TimeProject[] }> =>
    api.get('/time-tracking/projects', { params: { activeOnly: activeOnly !== false ? 'true' : 'false' } }).then(r => r.data),

  getTasks: (activeOnly?: boolean): Promise<{ success: boolean; data: TimeTask[] }> =>
    api.get('/time-tracking/tasks', { params: { activeOnly: activeOnly !== false ? 'true' : 'false' } }).then(r => r.data),

  addTask: (name: string): Promise<{ success: boolean; data: TimeTask }> =>
    api.post('/time-tracking/tasks', { name }).then(r => r.data),

  updateTask: (id: number, updates: { name?: string; is_active?: boolean; sort_order?: number }): Promise<{ success: boolean; data: TimeTask }> =>
    api.put(`/time-tracking/tasks/${id}`, updates).then(r => r.data),

  getSummary: (params?: {
    userId?: number; userIds?: number[]; startDate?: string; endDate?: string;
  }): Promise<{ success: boolean; data: ProjectSummary[] }> => {
    const query: Record<string, string> = {};
    if (params?.userId) query.userId = String(params.userId);
    if (params?.userIds?.length) query.userIds = params.userIds.join(',');
    if (params?.startDate) query.startDate = params.startDate;
    if (params?.endDate) query.endDate = params.endDate;
    return api.get('/time-tracking/summary', { params: query }).then(r => r.data);
  },

  getTimesheet: (params: {
    startDate: string; endDate: string; userId?: number;
  }): Promise<{ success: boolean; data: TimesheetRow[] }> => {
    const query: Record<string, string> = { startDate: params.startDate, endDate: params.endDate };
    if (params.userId) query.userId = String(params.userId);
    return api.get('/time-tracking/timesheet', { params: query }).then(r => r.data);
  },

  getUsers: (): Promise<{ success: boolean; data: Array<{ id: number; display_name: string; email: string }> }> =>
    api.get('/time-tracking/users').then(r => r.data),

  getFavorites: (): Promise<{ success: boolean; data: number[] }> =>
    api.get('/time-tracking/favorites').then(r => r.data),

  toggleFavorite: (projectId: number): Promise<{ success: boolean; data: { projectId: number; isFavorite: boolean } }> =>
    api.post('/time-tracking/favorites/toggle', { projectId }).then(r => r.data),

  getTeamDay: (): Promise<{ success: boolean; data: TeamDayUser[] }> =>
    api.get('/time-tracking/team-day').then(r => r.data),

  // Per-project task configuration
  getProjectTaskMap: (): Promise<{ success: boolean; data: Record<number, number[]> }> =>
    api.get('/time-tracking/project-task-map').then(r => r.data),

  getProjectTasks: (projectId: number): Promise<{ success: boolean; data: TimeTask[] }> =>
    api.get(`/time-tracking/projects/${projectId}/tasks`).then(r => r.data),

  toggleProjectTask: (projectId: number, taskId: number): Promise<{ success: boolean; data: { enabled: boolean } }> =>
    api.post(`/time-tracking/projects/${projectId}/tasks/${taskId}/toggle`).then(r => r.data),

  resetProjectTasks: (projectId: number): Promise<{ success: boolean; data: TimeTask[] }> =>
    api.post(`/time-tracking/projects/${projectId}/reset-tasks`).then(r => r.data),

  getCategories: (): Promise<{ success: boolean; data: TaskCategory[] }> =>
    api.get('/time-tracking/categories').then(r => r.data),

  createCategory: (name: string): Promise<{ success: boolean; data: TaskCategory }> =>
    api.post('/time-tracking/categories', { name }).then(r => r.data),

  updateCategoryDefaults: (categoryId: number, taskIds: number[]): Promise<{ success: boolean }> =>
    api.put(`/time-tracking/categories/${categoryId}/defaults`, { taskIds }).then(r => r.data),

};

export interface TeamDayUser {
  user_id: number;
  display_name: string;
  email: string;
  total_seconds: number;
  active_timer: { task_name: string; project_name: string; start_time: string } | null;
  tasks: Array<{ task_name: string; total_seconds: number }>;
}

export interface CalendarEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  attendeeCount: number;
  meetLink: string | null;
  status: string;
}

export const googleCalendarApi = {
  getAuthUrl: (): Promise<{ success: boolean; data: { url: string } }> =>
    api.get('/google-calendar/auth').then(r => r.data),

  // v54: enriched response. Backward compatible — existing consumers
  // only read `.connected`. New fields drive the Connected Accounts
  // panel's scope-mismatch warning.
  getStatus: (): Promise<{ success: boolean; data: {
    connected: boolean;
    granted_scopes: string | null;
    expected_scopes: string[];
    missing_scopes: string[];
    scope_tracking_unknown: boolean;
    expires_at: string | null;
    has_refresh_token: boolean;
  } }> =>
    api.get('/google-calendar/status').then(r => r.data),

  getTodayEvents: (): Promise<{ success: boolean; data: { connected: boolean; events: CalendarEvent[]; error?: string } }> =>
    api.get('/google-calendar/events/today').then(r => r.data),

  getWeekEvents: (): Promise<{ success: boolean; data: { connected: boolean; events: Record<string, CalendarEvent[]>; error?: string } }> =>
    api.get('/google-calendar/events/week').then(r => r.data),

  disconnect: (): Promise<{ success: boolean }> =>
    api.delete('/google-calendar/disconnect').then(r => r.data),
};


// ── AI Assistant API ──────────────────────────────────────────────────
export interface AIConversation {
  id: string;
  title: string;
  contextType: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  tokensUsed: number;
  model: string | null;
  createdAt: string;
}

export interface AIUsageStats {
  tokensToday: number;
  tokensThisMonth: number;
  dailyLimit: number;
  monthlyLimit: number;
  remainingToday: number;
  remainingThisMonth: number;
}

// ── Fathom / Call Intelligence ──

export const fathomApi = {
  getStatus: (): Promise<any> =>
    api.get('/fathom/status').then(r => r.data?.data ?? r.data),

  testConnection: (): Promise<any> =>
    api.post('/fathom/test').then(r => r.data.data),

  sync: (since?: string): Promise<any> =>
    api.post('/fathom/sync', { since }).then(r => r.data.data),

  getMeetings: (params?: {
    department?: string; search?: string; startDate?: string; endDate?: string; limit?: number; offset?: number;
  }): Promise<{ meetings: any[]; total: number }> =>
    api.get('/fathom/meetings', { params }).then(r => r.data?.data ?? r.data),

  getTriggerOptions: (): Promise<{ departments: string[]; recorders: any[]; members: any[] }> =>
    api.get('/fathom/trigger-options').then(r => r.data?.data ?? r.data),

  getTriggerSamples: (params?: { limit?: number; offset?: number; includeMetrics?: boolean; contentMode?: 'summary' | 'transcript' | 'both'; search?: string; department?: string; recorderEmail?: string }): Promise<{ samples: any[]; total?: number; limit?: number; offset?: number; nextOffset?: number; hasMore?: boolean; skipped?: number; payloadErrors?: Array<{ recording_id: number; error: string }> }> =>
    api.get('/fathom/trigger-samples', { params }).then(r => r.data?.data ?? r.data),

  getTranscript: (id: number): Promise<any[]> =>
    api.get(`/fathom/meetings/${id}/transcript`).then(r => r.data?.data ?? r.data),

  getGrade: (id: number): Promise<any> =>
    api.get(`/fathom/meetings/${id}/grade`).then(r => r.data?.data ?? r.data),

  gradeAll: (): Promise<{ graded: number; errors: string[] }> =>
    api.post('/fathom/meetings/grade-all').then(r => r.data?.data ?? r.data),

  updateMeeting: (id: number, data: { department?: string }): Promise<any> =>
    api.put(`/fathom/meetings/${id}`, data).then(r => r.data?.data ?? r.data),

  getReviews: (params?: { department?: string; limit?: number; offset?: number }): Promise<{ reviews: any[]; total: number }> =>
    api.get('/fathom/reviews', { params }).then(r => r.data?.data ?? r.data),

  generateReview: (department: string, weekStart?: string, weekEnd?: string): Promise<any> =>
    api.post('/fathom/reviews/generate', { department, weekStart, weekEnd }).then(r => r.data?.data ?? r.data),

  getContent: (params?: { contentType?: string; status?: string; limit?: number; offset?: number }): Promise<{ drafts: any[]; total: number }> =>
    api.get('/fathom/content', { params }).then(r => r.data?.data ?? r.data),

  generateContent: (weekStart?: string, weekEnd?: string, contentType?: string): Promise<any> =>
    api.post('/fathom/content/generate', { weekStart, weekEnd, contentType }).then(r => r.data?.data ?? r.data),

  updateContent: (id: number, data: { title?: string; body_markdown?: string; status?: string }): Promise<any> =>
    api.put(`/fathom/content/${id}`, data).then(r => r.data?.data ?? r.data),

  deleteContent: (id: number): Promise<any> =>
    api.delete(`/fathom/content/${id}`).then(r => r.data),

  getSettings: (): Promise<any> =>
    api.get('/fathom/settings').then(r => r.data?.data ?? r.data),

  updateSettings: (data: Record<string, any>): Promise<any> =>
    api.put('/fathom/settings', data).then(r => r.data),

  // Teams
  reassignDepartments: (): Promise<{ updated: number; unchanged: number }> =>
    api.post('/fathom/meetings/reassign-departments').then(r => r.data?.data ?? r.data),

  // Analytics
  getTeamAnalytics: (params?: { department?: string; startDate?: string; endDate?: string }): Promise<any> =>
    api.get('/fathom/analytics/team', { params }).then(r => r.data?.data ?? r.data),

  getPersonAnalytics: (userId: number, params?: { startDate?: string; endDate?: string }): Promise<any> =>
    api.get(`/fathom/analytics/person/${userId}`, { params }).then(r => r.data?.data ?? r.data),

  getAnalyticsTrends: (params?: { userId?: number; department?: string; weeks?: number }): Promise<any[]> =>
    api.get('/fathom/analytics/trends', { params }).then(r => r.data?.data ?? r.data),

  backfillMetrics: (): Promise<any> =>
    api.post('/fathom/analytics/backfill').then(r => r.data?.data ?? r.data),

  // Scorecards
  getPersonScorecard: (userId: number, params?: { startDate?: string; endDate?: string }): Promise<any> =>
    api.get(`/fathom/scorecards/person/${userId}`, { params }).then(r => r.data?.data ?? r.data),

  getDepartmentScorecard: (params?: { department?: string; startDate?: string; endDate?: string }): Promise<any> =>
    api.get('/fathom/scorecards/department', { params }).then(r => r.data?.data ?? r.data),

  getScorecardTrends: (params?: { userId?: number; department?: string; weeks?: number }): Promise<any[]> =>
    api.get('/fathom/scorecards/trends', { params }).then(r => r.data?.data ?? r.data),
};

export interface AIModelOption {
  id: string;
  label: string;
  provider: 'openai' | 'anthropic';
  tier: 'high' | 'standard' | 'fast';
  description: string;
}

export interface AISettingsData {
  provider: 'openai' | 'anthropic';
  model: string;
  modelLite: string;
  enabled: boolean;
  openaiKeySet: boolean;
  anthropicKeySet: boolean;
}

export interface AIInstruction {
  id: number;
  function_key: string;
  label: string;
  description: string | null;
  instructions: string;
  enabled: number;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

export const aiApi = {
  chat: (params: { conversationId?: string | null; message: string; contextType?: string }): Promise<{
    conversationId: string;
    messageId: string;
    content: string;
    title: string | null;
    tokensUsed: number;
  }> => api.post('/ai/chat', params).then(r => r.data.data),

  getInsights: (context: string): Promise<{ insights: string[]; cached: boolean; tokensUsed: number }> =>
    api.get('/ai/insights', { params: { context } }).then(r => r.data.data),

  getDigest: (week?: string): Promise<{ digest: string; cached: boolean; tokensUsed: number }> =>
    api.get('/ai/digest', { params: { week } }).then(r => r.data.data),

  getConversations: (): Promise<AIConversation[]> =>
    api.get('/ai/conversations').then(r => r.data.data),

  getConversation: (id: string): Promise<{ conversation: AIConversation; messages: AIMessage[] }> =>
    api.get(`/ai/conversations/${id}`).then(r => r.data.data),

  deleteConversation: (id: string): Promise<{ success: boolean }> =>
    api.delete(`/ai/conversations/${id}`).then(r => r.data.data),

  getUsage: (): Promise<AIUsageStats> =>
    api.get('/ai/usage').then(r => r.data.data),

  getAdminUsage: (): Promise<Array<{
    userId: number; displayName: string; email: string; roleName: string | null;
    tokensToday: number; tokensThisMonth: number; tokensAllTime: number;
  }>> => api.get('/ai/usage/admin').then(r => r.data.data),

  getSettings: (): Promise<{ settings: AISettingsData; models: AIModelOption[] }> =>
    api.get('/ai/settings').then(r => r.data.data),

  updateSettings: (data: Partial<Pick<AISettingsData, 'provider' | 'model' | 'modelLite' | 'enabled'>>): Promise<{ settings: AISettingsData; models: AIModelOption[] }> =>
    api.put('/ai/settings', data).then(r => r.data.data),

  getInstructions: (): Promise<{ success: boolean; data: AIInstruction[] }> =>
    api.get('/ai/instructions').then(r => r.data),

  updateInstruction: (key: string, data: { instructions?: string; enabled?: boolean }): Promise<{ success: boolean; data: AIInstruction }> =>
    api.put(`/ai/instructions/${key}`, data).then(r => r.data),
};

// ── Integration Settings ──

export const integrationsApi = {
  getAll: (): Promise<any[]> =>
    api.get('/integrations').then(r => r.data?.data ?? r.data),

  update: (name: string, data: { config?: Record<string, any>; is_enabled?: boolean }): Promise<any> =>
    api.put(`/integrations/${name}`, data).then(r => r.data?.data ?? r.data),

  test: (name: string): Promise<{ success: boolean; message: string }> =>
    api.post(`/integrations/${name}/test`).then(r => r.data?.data ?? r.data),
};

// ── Time Reports (Unified Reporting) ──

export type ReportDataSource = 'native' | 'clockify' | 'merged';

export interface ProjectTimeRow {
  projectId: string;
  projectName: string;
  onCallHours: number;
  implementationHours: number;
  supportHours: number;
  documentingFlowchartingHours: number;
  otherHours: number;
  totalHours: number;
}

export interface ProjectLaborCostRow {
  projectId: string;
  laborCostOnCall: number;
  laborCostOffCall: number;
}

export interface UserProjectHoursRow {
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  onCallHours: number;
  offCallHours: number;
  totalHours: number;
  implementationHours: number;
  supportHours: number;
  documentingHours: number;
  otherHours: number;
}

export interface TimeReportSettings {
  pph_on_call: number | null;
  pph_off_call: number | null;
  operating_costs: number | null;
  active_clients: number | null;
  team_capacity_hours: number | null;
}

export interface TimeReportLaborRate {
  user_id: string;
  user_name: string;
  hourly_rate: number;
}

export interface SavedView {
  id: number;
  name: string;
  filters: any;
  position: number;
  user_id: number | null;
  created_at: string;
}

export const timeReportsApi = {
  getProjects: (params: {
    source?: ReportDataSource; userIds?: string[]; excludeProjects?: string[];
    startDate?: string; endDate?: string;
  } = {}): Promise<{ success: boolean; data: ProjectTimeRow[] }> => {
    const query = new URLSearchParams();
    if (params.source) query.set('source', params.source);
    if (params.userIds?.length) query.set('userIds', params.userIds.join(','));
    if (params.excludeProjects?.length) query.set('excludeProjects', params.excludeProjects.join(','));
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    return api.get(`/time-reports/projects?${query.toString()}`).then(r => r.data);
  },

  getLaborCosts: (params: {
    source?: ReportDataSource; userIds?: string[]; excludeProjects?: string[];
    startDate?: string; endDate?: string;
  } = {}): Promise<{ success: boolean; data: ProjectLaborCostRow[] }> => {
    const query = new URLSearchParams();
    if (params.source) query.set('source', params.source);
    if (params.userIds?.length) query.set('userIds', params.userIds.join(','));
    if (params.excludeProjects?.length) query.set('excludeProjects', params.excludeProjects.join(','));
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    return api.get(`/time-reports/labor-costs?${query.toString()}`).then(r => r.data);
  },

  getUserHours: (params: {
    source?: ReportDataSource; startDate?: string; endDate?: string;
  } = {}): Promise<{ success: boolean; data: UserProjectHoursRow[] }> => {
    const query = new URLSearchParams();
    if (params.source) query.set('source', params.source);
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    return api.get(`/time-reports/user-hours?${query.toString()}`).then(r => r.data);
  },

  getSettings: (): Promise<{ success: boolean; data: { settings: TimeReportSettings; laborRates: TimeReportLaborRate[] } }> =>
    api.get('/time-reports/settings').then(r => r.data),

  updateSettings: (data: Partial<TimeReportSettings>): Promise<{ success: boolean; data: TimeReportSettings }> =>
    api.put('/time-reports/settings', data).then(r => r.data),

  setLaborRate: (userId: string, userName: string, hourlyRate: number): Promise<{ success: boolean }> =>
    api.put(`/time-reports/labor-rates/${userId}`, { userName, hourlyRate }).then(r => r.data),

  getMRR: (): Promise<{ success: boolean; data: Array<{ project_id: string; project_name: string; mrr: number }> }> =>
    api.get('/time-reports/mrr').then(r => r.data),

  setMRR: (projectId: string, projectName: string, mrr: number): Promise<{ success: boolean }> =>
    api.put(`/time-reports/mrr/${projectId}`, { projectName, mrr }).then(r => r.data),

  getBudgets: (): Promise<{ success: boolean; data: Array<{ project_id: string; project_name: string; budgeted_on_call: number; budgeted_off_call: number }> }> =>
    api.get('/time-reports/budgets').then(r => r.data),

  setBudget: (projectId: string, projectName: string, budgetedOnCall: number, budgetedOffCall: number): Promise<{ success: boolean }> =>
    api.put(`/time-reports/budgets/${projectId}`, { projectName, budgetedOnCall, budgetedOffCall }).then(r => r.data),

  getSavedViews: (): Promise<{ success: boolean; data: SavedView[] }> =>
    api.get('/time-reports/views').then(r => r.data),

  createSavedView: (name: string, filters: any, personal?: boolean): Promise<{ success: boolean; data: { id: number } }> =>
    api.post('/time-reports/views', { name, filters, personal }).then(r => r.data),

};

// ── Operations API ──────────────────────────────────────────────────

export const operationsApi = {
  getClients: (filter?: 'active' | 'archived' | 'all'): Promise<{ success: boolean; data: OperationsClient[] }> =>
    api.get('/operations/clients', { params: { filter } }).then(r => r.data),

  getClient: (cardId: string): Promise<{ success: boolean; data: OperationsClient }> =>
    api.get(`/operations/clients/${cardId}`).then(r => r.data),

  updateClient: (cardId: string, data: Partial<OperationsClient>): Promise<{ success: boolean; data: OperationsClient }> =>
    api.patch(`/operations/clients/${cardId}`, data).then(r => r.data),

  createClient: (data: {
    title: string;
    implementer?: string;
    project_manager?: string;
    client_type?: string;
    slack_channel_id?: string;
    google_drive_id?: string;
    miro_space_url?: string;
    start_date?: string;
  }): Promise<{ success: boolean; data: OperationsClient }> =>
    api.post('/operations/clients', data).then(r => r.data),

  getClientDomains: (cardId: string): Promise<{ success: boolean; data: ClientDomain[] }> =>
    api.get(`/operations/clients/${cardId}/domains`).then(r => r.data),

  resolveClientByDomain: (domain: string): Promise<{ success: boolean; data: { card_id: string; title: string } | null }> =>
    api.get('/operations/resolve-client-by-domain', { params: { domain } }).then(r => r.data),

  addClientDomain: (cardId: string, type: 'domain' | 'email', value: string): Promise<{ success: boolean; data: ClientDomain }> =>
    api.post(`/operations/clients/${cardId}/domains`, { type, value }).then(r => r.data),

  removeClientDomain: (cardId: string, domainId: number): Promise<{ success: boolean }> =>
    api.delete(`/operations/clients/${cardId}/domains/${domainId}`).then(r => r.data),

  shiftActionPlanDates: (planId: number, newStartDate: string): Promise<{ success: boolean; data: ActionPlan }> =>
    api.put(`/operations/action-plans/${planId}/shift-dates`, { new_start_date: newStartDate }).then(r => r.data),

  getClientActionPlans: (cardId: string): Promise<{ success: boolean; data: ActionPlan[] }> =>
    api.get(`/operations/clients/${cardId}/action-plans`).then(r => r.data),

  createActionPlan: (cardId: string, data: {
    title: string;
    plan_type?: string;
    start_date: string;
    end_date: string;
    weeks: Array<{ week_number: number; label: string; notes?: string }>;
  }): Promise<{ success: boolean; data: ActionPlan }> =>
    api.post(`/operations/clients/${cardId}/action-plans`, data).then(r => r.data),

  getActionPlan: (planId: number): Promise<{ success: boolean; data: ActionPlan }> =>
    api.get(`/operations/action-plans/${planId}`).then(r => r.data),

  updateActionPlan: (planId: number, data: {
    title?: string; start_date?: string; end_date?: string; status?: string;
  }): Promise<{ success: boolean; data: ActionPlan }> =>
    api.put(`/operations/action-plans/${planId}`, data).then(r => r.data),

  deleteActionPlan: (planId: number): Promise<{ success: boolean }> =>
    api.delete(`/operations/action-plans/${planId}`).then(r => r.data),

  hardDeleteActionPlan: (planId: number): Promise<{ success: boolean }> =>
    api.delete(`/operations/action-plans/${planId}/permanent`).then(r => r.data),

  updateWeek: (planId: number, weekId: number, data: {
    label?: string; notes?: string; status?: string; pivoted_from?: string | null;
  }): Promise<{ success: boolean; data: ActionPlanWeek }> =>
    api.put(`/operations/action-plans/${planId}/weeks/${weekId}`, data).then(r => r.data),

  addWeek: (planId: number, data: {
    week_number: number; label: string; notes?: string;
  }): Promise<{ success: boolean; data: ActionPlanWeek }> =>
    api.post(`/operations/action-plans/${planId}/weeks`, data).then(r => r.data),

  deleteWeek: (planId: number, weekId: number): Promise<{ success: boolean }> =>
    api.delete(`/operations/action-plans/${planId}/weeks/${weekId}`).then(r => r.data),

  getActionPlanTemplates: (): Promise<{ success: boolean; data: Array<{ id: number; name: string; description: string | null; week_count: number; default_week_labels: string | null; created_by: number | null; created_at: string }> }> =>
    api.get('/operations/action-plan-templates').then(r => r.data),

  createActionPlanTemplate: (data: { name: string; description?: string; week_count?: number; default_week_labels?: string | string[] | null }): Promise<{ success: boolean; data: any }> =>
    api.post('/operations/action-plan-templates', data).then(r => r.data),

  applyActionPlanTemplate: (templateId: number, data: { card_id: string; title: string; start_date: string }): Promise<{ success: boolean; data: ActionPlan }> =>
    api.post(`/operations/action-plan-templates/${templateId}/apply`, data).then(r => r.data),

  getProcessAlerts: (): Promise<{ success: boolean; data: Array<{ id: number; template_id: number; name: string; status: string; due_date: string | null; template_name: string; client_card_id: string | null; started_at: string }> }> =>
    api.get('/operations/process-alerts').then(r => r.data),
};

// ── Sales API ────────────────────────────────────────────────────────

export const salesApi = {
  getStats: (): Promise<{ success: boolean; data: SalesHubStats }> =>
    api.get('/sales/stats').then(r => r.data),

  getPipelines: (): Promise<{ success: boolean; data: Pipeline[] }> =>
    api.get('/sales/pipelines').then(r => r.data),

  getPipelineStages: (pipelineId: number): Promise<{ success: boolean; data: PipelineStage[] }> =>
    api.get(`/sales/pipelines/${pipelineId}/stages`).then(r => r.data),
  getLeads: (params?: {
    page?: number; limit?: number; status?: string; source?: string; search?: string;
    pipeline?: boolean; pipeline_id?: number; pipeline_stage?: string;
  }): Promise<{ success: boolean; data: ScrapedLead[]; total: number }> =>
    api.get('/sales/leads', { params }).then(r => r.data),

  getLeadHistory: (leadId: number): Promise<{ success: boolean; data: LeadHistoryEntry[] }> =>
    api.get(`/sales/leads/${leadId}/history`).then(r => r.data),

  getPipelineSavedViews: (pipelineId?: number): Promise<{ success: boolean; data: PipelineSavedView[] }> =>
    api.get('/sales/pipelines/saved-views', { params: pipelineId != null ? { pipeline_id: pipelineId } : {} }).then(r => r.data),
  getCampaigns: (pipelineId?: number): Promise<{ success: boolean; data: SalesCampaign[] }> =>
    api.get('/sales/campaigns', { params: pipelineId != null ? { pipeline_id: pipelineId } : {} }).then(r => r.data),
  updateLeadStage: (id: number, stage: string, pipelineId?: number): Promise<{ success: boolean; data: ScrapedLead }> =>
    api.patch(`/sales/leads/${id}/stage`, { stage, ...(pipelineId != null ? { pipeline_id: pipelineId } : {}) }).then(r => r.data),

  getLead: (id: number): Promise<{ success: boolean; data: ScrapedLead }> =>
    api.get(`/sales/leads/${id}`).then(r => r.data),

  updateLead: (id: number, data: Partial<ScrapedLead>): Promise<{ success: boolean; data: ScrapedLead }> =>
    api.put(`/sales/leads/${id}`, data).then(r => r.data),

  deleteLead: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/sales/leads/${id}`).then(r => r.data),

  triggerLeadScrape: (config: {
    prospectType?: string; platforms?: string[]; companyType?: string;
    region?: string; states?: string[]; cities?: string[];
    country?: string; customDescription?: string; useAI?: boolean;
    maxResults?: number;
  }): Promise<{ success: boolean; data: { jobId: string } }> =>
    api.post('/sales/leads/scrape', config).then(r => r.data),

  bulkLeadAction: (ids: number[], action: 'delete' | 'stage' | 'update', data?: Record<string, any>): Promise<{ success: boolean; data: { updated: number; errors: string[] } }> =>
    api.post('/sales/leads/bulk', { ids, action, data }).then(r => r.data),

  getEvents: (params?: {
    page?: number; limit?: number; status?: string; category?: string;
    search?: string; startDate?: string; endDate?: string;
  }): Promise<{ success: boolean; data: ScrapedEvent[]; total: number }> =>
    api.get('/sales/events', { params }).then(r => r.data),

  updateEvent: (id: number, data: Partial<ScrapedEvent>): Promise<{ success: boolean; data: ScrapedEvent }> =>
    api.put(`/sales/events/${id}`, data).then(r => r.data),

  triggerEventScrape: (config: {
    focus?: string[]; eventTypes?: string[]; platforms?: string[];
    region?: string; states?: string[]; country?: string;
    customTerms?: string; maxResults?: number;
  }): Promise<{ success: boolean; data: { jobId: string } }> =>
    api.post('/sales/events/scrape', config).then(r => r.data),

  getFollowUps: (upcoming = true): Promise<{ success: boolean; data: LeadFollowUp[] }> =>
    api.get('/sales/follow-ups', { params: { upcoming } }).then(r => r.data),

  completeFollowUp: (id: number): Promise<{ success: boolean }> =>
    api.patch(`/sales/follow-ups/${id}/complete`).then(r => r.data),

  getDashboardStats: (): Promise<{ success: boolean; data: SalesDashboardStats }> =>
    api.get('/sales/dashboard-stats').then(r => r.data),

  // Lead contacts
  getLeadContacts: (leadId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/sales/leads/${leadId}/contacts`).then(r => r.data),

  addLeadContact: (leadId: number, contactId: number): Promise<{ success: boolean }> =>
    api.post(`/sales/leads/${leadId}/contacts`, { contact_id: contactId }).then(r => r.data),

  removeLeadContact: (leadId: number, contactId: number): Promise<{ success: boolean }> =>
    api.delete(`/sales/leads/${leadId}/contacts/${contactId}`).then(r => r.data),

  getLeadFiles: (leadId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/sales/leads/${leadId}/files`).then(r => r.data),

  addLeadFile: (leadId: number, data: { file_name: string; file_path: string; file_type?: string; file_size?: number }): Promise<{ success: boolean; data: any }> =>
    api.post(`/sales/leads/${leadId}/files`, data).then(r => r.data),

  deleteLeadFile: (leadId: number, fileId: number): Promise<{ success: boolean }> =>
    api.delete(`/sales/leads/${leadId}/files/${fileId}`).then(r => r.data),

  // Manual lead creation
  createLead: (data: Record<string, any>) =>
    api.post('/sales/leads', data),

  // CSV lead import
  importLeadsPreview: (formData: FormData) =>
    api.post('/sales/leads/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  importLeads: (formData: FormData) =>
    api.post('/sales/leads/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  checkLeadDuplicates: (leadIds: number[]) =>
    api.post('/sales/leads/check-duplicates', { lead_ids: leadIds }).then(r => r.data),
  importLeadsToContacts: (leadIds: number[], options?: { tag_ids?: number[]; new_tag_name?: string; campaign_id?: number; pipeline_id?: number; pipeline_stage?: string; duplicate_action?: string }) =>
    api.post('/sales/leads/import-to-contacts', { lead_ids: leadIds, ...options }).then(r => r.data),
};

// ── EOS ────────────────────────────────────────────────────────────────
export const eosApi = {
  getMeetings: (params?: {
    page?: number; limit?: number; type?: string; status?: string; search?: string;
  }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/eos/meetings', { params }).then(r => r.data),

  getMeeting: (id: string): Promise<{ success: boolean; data: any }> =>
    api.get(`/eos/meetings/${id}`).then(r => r.data),

  createMeeting: (data: {
    title: string; meeting_type: string; meeting_date: string; template_id?: string;
  }): Promise<{ success: boolean; data: any }> =>
    api.post('/eos/meetings', data).then(r => r.data),

  updateMeeting: (id: string, data: any): Promise<{ success: boolean; data: any }> =>
    api.put(`/eos/meetings/${id}`, data).then(r => r.data),

  deleteMeeting: (id: string): Promise<{ success: boolean }> =>
    api.delete(`/eos/meetings/${id}`).then(r => r.data),

  // Documents
  getDocument: (meetingId: string): Promise<{ success: boolean; data: any }> =>
    api.get(`/eos/meetings/${meetingId}/document`).then(r => r.data),

  updateDocument: (meetingId: string, content_json: any): Promise<{ success: boolean }> =>
    api.put(`/eos/meetings/${meetingId}/document`, { content_json }).then(r => r.data),

  getSnapshots: (meetingId: string): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/eos/meetings/${meetingId}/snapshots`).then(r => r.data),

  createSnapshot: (meetingId: string, data: {
    snapshot_type: string; department: string; snapshot_data: any;
  }): Promise<{ success: boolean; data: { id: string } }> =>
    api.post(`/eos/meetings/${meetingId}/snapshots`, data).then(r => r.data),

  // Rocks
  getRocks: (params?: {
    page?: number; limit?: number; department?: string; quarter?: string;
    year?: number; status?: string; owner?: string;
  }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/eos/rocks', { params }).then(r => r.data),

  createRock: (data: {
    title: string; description?: string; department: string; owner?: string;
    quarter: string; year: number; due_date?: string;
  }): Promise<{ success: boolean; data: any }> =>
    api.post('/eos/rocks', data).then(r => r.data),

  updateRock: (id: string, data: any): Promise<{ success: boolean; data: any }> =>
    api.put(`/eos/rocks/${id}`, data).then(r => r.data),

  deleteRock: (id: string): Promise<{ success: boolean }> =>
    api.delete(`/eos/rocks/${id}`).then(r => r.data),

  // IDS Issues
  getIssues: (params?: {
    page?: number; limit?: number; department?: string; status?: string;
    priority?: string; meeting_id?: string; search?: string;
  }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/eos/issues', { params }).then(r => r.data),

  createIssue: (data: {
    title: string; description?: string; department: string;
    priority?: string; meeting_id?: string;
  }): Promise<{ success: boolean; data: any }> =>
    api.post('/eos/issues', data).then(r => r.data),

  updateIssue: (id: string, data: any): Promise<{ success: boolean; data: any }> =>
    api.put(`/eos/issues/${id}`, data).then(r => r.data),

  toggleVote: (issueId: string): Promise<{ success: boolean; data: { voted: boolean; votes: { count: number; voters: Array<{ user_id: number; display_name: string }> } } }> =>
    api.post(`/eos/issues/${issueId}/vote`).then(r => r.data),

  getVotesBulk: (issueIds: string[]): Promise<{ success: boolean; data: Record<string, { count: number; voters: Array<{ user_id: number; display_name: string }> }> }> =>
    api.get('/eos/issues/votes/bulk', { params: { ids: issueIds.join(',') } }).then(r => r.data),

  // Issue Notes
  getIssueNotes: (issueId: string): Promise<{ success: boolean; data: Array<{ id: string; issue_id: string; user_id: number; content: string; created_at: string; author_name: string }> }> =>
    api.get(`/eos/issues/${issueId}/notes`).then(r => r.data),

  addIssueNote: (issueId: string, content: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/eos/issues/${issueId}/notes`, { content }).then(r => r.data),

  // Rock Notes
  getRockNotes: (rockId: string): Promise<{ success: boolean; data: Array<{ id: string; rock_id: string; user_id: number; content: string; created_at: string; author_name: string }> }> =>
    api.get(`/eos/rocks/${rockId}/notes`).then(r => r.data),

  addRockNote: (rockId: string, content: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/eos/rocks/${rockId}/notes`, { content }).then(r => r.data),

  // AI
  summarizeMeeting: (meetingId: string): Promise<{ success: boolean; data: { summary: string }; error?: string }> =>
    api.post(`/eos/meetings/${meetingId}/summarize`).then(r => r.data),

  getTemplates: (params?: { department?: string; meeting_type?: string }): Promise<{ success: boolean; data: any[] }> =>
    api.get('/eos/templates', { params }).then(r => r.data),

  getTemplate: (id: string): Promise<{ success: boolean; data: any }> =>
    api.get(`/eos/templates/${id}`).then(r => r.data),

  createTemplate: (data: {
    name: string; meeting_type: string; department: string; content_json?: string; is_default?: boolean;
  }): Promise<{ success: boolean; data: any }> =>
    api.post('/eos/templates', data).then(r => r.data),

  updateTemplate: (id: string, data: {
    name?: string; content_json?: string; is_default?: boolean; department?: string;
  }): Promise<{ success: boolean; data: any }> =>
    api.put(`/eos/templates/${id}`, data).then(r => r.data),

  deleteTemplate: (id: string): Promise<{ success: boolean }> =>
    api.delete(`/eos/templates/${id}`).then(r => r.data),

  duplicateTemplate: (id: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/eos/templates/${id}/duplicate`).then(r => r.data),

  // Kickoff Entries
  getKickoffEntries: (meetingId: string): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/eos/meetings/${meetingId}/kickoff-entries`).then(r => r.data),

  addKickoffEntry: (meetingId: string, data: { entry_type: string; title: string; description?: string }): Promise<{ success: boolean; data: any }> =>
    api.post(`/eos/meetings/${meetingId}/kickoff-entries`, data).then(r => r.data),

  deleteKickoffEntry: (id: string): Promise<{ success: boolean }> =>
    api.delete(`/eos/kickoff-entries/${id}`).then(r => r.data),

  // Recurring Meetings
  getRecurringMeetings: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/eos/recurring-meetings').then(r => r.data),

  createRecurringMeeting: (data: Record<string, any>): Promise<{ success: boolean; data: any }> =>
    api.post('/eos/recurring-meetings', data).then(r => r.data),

  updateRecurringMeeting: (id: string, data: Record<string, any>): Promise<{ success: boolean; data: any }> =>
    api.put(`/eos/recurring-meetings/${id}`, data).then(r => r.data),

  deleteRecurringMeeting: (id: string): Promise<{ success: boolean }> =>
    api.delete(`/eos/recurring-meetings/${id}`).then(r => r.data),

  runRecurringMeetingNow: (id: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/eos/recurring-meetings/${id}/run-now`).then(r => r.data),

  // Attendance
  getAttendance: (meetingId: string): Promise<{ success: boolean; data: Array<{ id: number; user_id: number; status: string; user_name?: string; user_email?: string }> }> =>
    api.get(`/eos/meetings/${meetingId}/attendance`).then(r => r.data),

  addAttendee: (meetingId: string, userId: number, status?: 'present' | 'absent' | 'late'): Promise<{ success: boolean; data: any }> =>
    api.post(`/eos/meetings/${meetingId}/attendance`, { user_id: userId, status: status || 'present' }).then(r => r.data),

  removeAttendee: (meetingId: string, userId: number): Promise<{ success: boolean }> =>
    api.delete(`/eos/meetings/${meetingId}/attendance/${userId}`).then(r => r.data),

  updateAttendanceStatus: (meetingId: string, userId: number, status: 'present' | 'absent' | 'late'): Promise<{ success: boolean; data: any }> =>
    api.patch(`/eos/meetings/${meetingId}/attendance/${userId}`, { status }).then(r => r.data),
};

// ── Processes API ─────────────────────────────────────────────────────

export const processesApi = {
  // AI Agent HR
  getAiAgents: (): Promise<{ success: boolean; data: Array<{
    id: number;
    name: string;
    job_title: string;
    department?: string | null;
    description?: string | null;
    status?: string;
    access_level?: string;
    avatar_icon?: string | null;
    model_preference?: string | null;
    tone?: string | null;
    job_description_html?: string | null;
    responsibilities_json?: string | null;
    success_criteria_json?: string | null;
    escalation_rules_json?: string | null;
    review_policy_json?: string | null;
    output_schema_json?: string | null;
    default_reviewer_user_id?: number | null;
    risk_level: string;
    is_active: number;
    tools?: Array<{ tool_key: string; label: string; requires_approval: number }>;
    rules?: Array<{ id: number; rule_type: string; title: string; body: string }>;
    knowledge_access?: Array<{ tag_name: string; access_level: string; document_count?: number }>;
    documents?: Array<{ id: number; title: string; summary?: string; tags_json?: string; status?: string; updated_at?: string }>;
  }> }> =>
    api.get('/processes/ai-agents').then(r => r.data),
  getAiAgentTools: (): Promise<{ success: boolean; data: Array<{ tool_key: string; label: string; description?: string; maps_to_action?: string; risk_level: string; requires_approval: number }> }> =>
    api.get('/processes/ai-agents/tools').then(r => r.data),
  draftAiAgentProfile: (prompt: string): Promise<{ success: boolean; data: any }> =>
    api.post('/processes/ai-agents/draft-profile', { prompt }).then(r => r.data),
  getAiAgentRuns: (params?: { agent_id?: number; instance_id?: number; status?: string; limit?: number }): Promise<{ success: boolean; data: any[] }> =>
    api.get('/processes/ai-agent-runs', { params }).then(r => r.data),
  approveAiAgentRun: (id: number, notes?: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agent-runs/${id}/approve`, { notes }).then(r => r.data),
  rejectAiAgentRun: (id: number, notes?: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agent-runs/${id}/reject`, { notes }).then(r => r.data),
  retryAiAgentRun: (id: number): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agent-runs/${id}/retry`).then(r => r.data),
  getAiAgentUsage: (id: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/processes/ai-agents/${id}/usage`).then(r => r.data),
  createAiAgent: (data: {
    name: string;
    job_title?: string;
    department?: string;
    description?: string;
    tone?: string;
    default_reviewer_user_id?: number | null;
    risk_level?: string;
    status?: string;
    access_level?: string;
    avatar_icon?: string;
    job_description_html?: string;
    responsibilities_json?: string;
    success_criteria_json?: string;
    escalation_rules_json?: string;
    review_policy_json?: string;
    output_schema_json?: string;
  }): Promise<{ success: boolean; data: any }> =>
    api.post('/processes/ai-agents', data).then(r => r.data),
  updateAiAgent: (id: number, data: Record<string, any>): Promise<{ success: boolean; data: any }> =>
    api.put(`/processes/ai-agents/${id}`, data).then(r => r.data),
  updateAiAgentRules: (id: number, rules: Array<{ rule_type?: string; title: string; body: string }>): Promise<{ success: boolean; data: any }> =>
    api.put(`/processes/ai-agents/${id}/rules`, { rules }).then(r => r.data),
  updateAiAgentTools: (id: number, tools: Array<{ tool_key: string; workflow_type?: string; requires_approval?: boolean | number }>): Promise<{ success: boolean; data: any }> =>
    api.put(`/processes/ai-agents/${id}/tools`, { tools }).then(r => r.data),
  updateAiAgentKnowledgeAccess: (id: number, rules: Array<{ tag_name: string; access_level: string }>): Promise<{ success: boolean; data: any }> =>
    api.put(`/processes/ai-agents/${id}/knowledge-access`, { rules }).then(r => r.data),
  attachAiAgentDocument: (id: number, data: { title: string; content_html?: string; content_text?: string; tags?: string[] }): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agents/${id}/documents`, data).then(r => r.data),
  publishAiAgent: (id: number): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agents/${id}/publish`).then(r => r.data),
  archiveAiAgent: (id: number): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agents/${id}/archive`).then(r => r.data),
  duplicateAiAgent: (id: number): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/ai-agents/${id}/duplicate`).then(r => r.data),
  deleteAiAgent: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/ai-agents/${id}`).then(r => r.data),

  // Templates
  getTemplates: (filter?: { is_active?: string; category?: string; status?: string }): Promise<{ success: boolean; data: ProcessTemplate[] }> =>
    api.get('/processes/templates', { params: filter }).then(r => r.data),
  getTemplate: (id: number): Promise<{ success: boolean; data: ProcessTemplate }> =>
    api.get(`/processes/templates/${id}`).then(r => r.data),
  createTemplate: (data: { name: string; description?: string; category?: string; trigger_type?: string }): Promise<{ success: boolean; data: ProcessTemplate }> =>
    api.post('/processes/templates', data).then(r => r.data),
  updateTemplate: (id: number, data: Partial<ProcessTemplate>): Promise<{ success: boolean; data: ProcessTemplate }> =>
    api.put(`/processes/templates/${id}`, data).then(r => r.data),
  deleteTemplate: (id: number, permanent = false): Promise<{ success: boolean }> =>
    api.delete(`/processes/templates/${id}`, { params: permanent ? { permanent: 'true' } : undefined }).then(r => r.data),
  updateStep: (stepId: number, data: Partial<ProcessStep>): Promise<{ success: boolean; data: ProcessStep }> =>
    api.put(`/processes/steps/${stepId}`, data).then(r => r.data),
  addField: (templateId: number, data: Partial<ProcessCustomField>): Promise<{ success: boolean; data: ProcessCustomField }> =>
    api.post(`/processes/templates/${templateId}/fields`, data).then(r => r.data),
  updateField: (fieldId: number, data: Partial<ProcessCustomField>): Promise<{ success: boolean; data: ProcessCustomField }> =>
    api.put(`/processes/fields/${fieldId}`, data).then(r => r.data),
  deleteField: (fieldId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/fields/${fieldId}`).then(r => r.data),

  // Roles
  addRole: (templateId: number, data: { name: string; description?: string }): Promise<{ success: boolean; data: ProcessRole }> =>
    api.post(`/processes/templates/${templateId}/roles`, data).then(r => r.data),
  updateRole: (roleId: number, data: Partial<ProcessRole>): Promise<{ success: boolean; data: ProcessRole }> =>
    api.put(`/processes/roles/${roleId}`, data).then(r => r.data),
  deleteRole: (roleId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/roles/${roleId}`).then(r => r.data),

  // Schedules
  addSchedule: (templateId: number, data: Partial<ProcessRecurringSchedule>): Promise<{ success: boolean; data: ProcessRecurringSchedule }> =>
    api.post(`/processes/templates/${templateId}/schedules`, data).then(r => r.data),
  deleteSchedule: (schedId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/schedules/${schedId}`).then(r => r.data),

  // Instances
  startInstance: (data: { template_id: number; name?: string; client_card_id?: string; lead_id?: number; contact_id?: number; field_values?: Record<string, any> }): Promise<{ success: boolean; data: ProcessInstance }> =>
    api.post('/processes/instances/start', data).then(r => r.data),
  getInstances: (filter?: { status?: string; template_id?: number; client_card_id?: string; lead_id?: number; contact_id?: number; limit?: number }): Promise<{ success: boolean; data: ProcessInstance[] }> =>
    api.get('/processes/instances', { params: filter }).then(r => r.data),
  getInstance: (id: number): Promise<{ success: boolean; data: ProcessInstance }> =>
    api.get(`/processes/instances/${id}`).then(r => r.data),
  updateInstance: (id: number, data: { action: 'cancel' | 'pause' | 'resume' }): Promise<{ success: boolean; data: ProcessInstance }> =>
    api.put(`/processes/instances/${id}`, data).then(r => r.data),
  restartInstance: (id: number): Promise<{ success: boolean; data: ProcessInstance }> =>
    api.post(`/processes/instances/${id}/restart`).then(r => r.data),
  getInstanceActivity: (id: number): Promise<{ success: boolean; data: ProcessActivityEntry[] }> =>
    api.get(`/processes/instances/${id}/activity`).then(r => r.data),
  completeTask: (taskId: number, fieldValues?: Array<{ fieldId: number; value: any }>): Promise<{ success: boolean; data: ProcessTaskInstance }> =>
    api.post(`/processes/tasks/${taskId}/complete`, { fieldValues }).then(r => r.data),
  skipTask: (taskId: number): Promise<{ success: boolean; data: ProcessTaskInstance }> =>
    api.post(`/processes/tasks/${taskId}/skip`).then(r => r.data),
  retryTask: (taskId: number): Promise<{ success: boolean; data: ProcessTaskInstance }> =>
    api.post(`/processes/tasks/${taskId}/retry`).then(r => r.data),
  getTeamUsers: (): Promise<{ success: boolean; data: Array<{ id: number; display_name: string; email: string; avatar_url?: string }> }> =>
    api.get('/processes/team-users').then(r => r.data),

  testAction: (data: { actionType: string; configJson: Record<string, any>; testContext: Record<string, any>; previewOnly?: boolean }): Promise<any> =>
    // 0b-02c: backend nests result under .data; unpack to preserve old spread shape.
    api.post('/processes/test-action', data).then(r => ({ success: r.data.success, ...r.data.data })),

  // Template Library
  extractedStages: (templateId: number): Promise<{ success: boolean; data: Array<{ step_id: number; stage_key: string; stage_group: string; label?: string; color?: string; is_terminal: boolean; sort_order: number }> }> =>
    api.get(`/processes/templates/${templateId}/extracted-stages`).then(r => r.data),
  publishToPipeline: (templateId: number, data: { pipeline_name?: string; process_type?: string; group_metadata?: Record<string, { color?: string; is_terminal?: boolean; sort_order?: number }> }): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/templates/${templateId}/publish-to-pipeline`, data).then(r => r.data),
  getStageGroupMetadata: (templateId: number): Promise<{ success: boolean; data: Record<string, { color?: string; is_terminal?: boolean; sort_order?: number }> }> =>
    api.get(`/processes/templates/${templateId}/stage-group-metadata`).then(r => r.data),
  saveStageGroupMetadata: (templateId: number, metadata: Record<string, { color?: string; is_terminal?: boolean; sort_order?: number }>): Promise<{ success: boolean }> =>
    api.put(`/processes/templates/${templateId}/stage-group-metadata`, { metadata }).then(r => r.data),

  // Template stage management
  getTemplateStageGroups: (templateId: number) =>
    api.get(`/processes/templates/${templateId}/stage-groups`).then(r => r.data),
  updateTemplateStageGroups: (templateId: number, groups: Array<{ id?: number; name: string; color?: string; sort_order?: number; is_terminal?: boolean }>) =>
    api.put(`/processes/templates/${templateId}/stage-groups`, { groups }).then(r => r.data),
  getTemplateStages: (templateId: number) =>
    api.get(`/processes/templates/${templateId}/stages`).then(r => r.data),
  updateTemplateStages: (templateId: number, stages: Array<{ id?: number; stage_key: string; label: string; group_id?: number | null; color?: string; sort_order?: number; is_terminal?: boolean }>) =>
    api.put(`/processes/templates/${templateId}/stages`, { stages }).then(r => r.data),

  // Automation Logs
  getAutomationLog: (filters?: { instance_id?: number; lead_id?: number; template_id?: number; status?: string; event_type?: string; limit?: number; offset?: number }): Promise<{ success: boolean; data: any[] }> =>
    api.get('/processes/automation-log', { params: filters }).then(r => r.data),

  // Test Lead Management
  createTestLead: (templateId: number, data: { email?: string; phone?: string; company_name?: string; contact_name?: string; skip_delays?: boolean }): Promise<{ success: boolean; data: { lead_id: number; instance_id: number; lead: any; instance: any } }> =>
    api.post(`/processes/templates/${templateId}/test-lead`, data).then(r => r.data),
  deleteTestLead: (leadId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/test-lead/${leadId}`).then(r => r.data),
  restartTestCampaign: (templateId: number, leadId: number, skipDelays?: boolean): Promise<{ success: boolean; data: { lead_id: number; instance_id: number; instance: any } }> =>
    api.post(`/processes/templates/${templateId}/restart-test`, { lead_id: leadId, skip_delays: skipDelays }).then(r => r.data),

  // Instance Field Values
  updateInstanceFieldValues: (instanceId: number, fieldValues: Array<{ field_id: number; value: any }>): Promise<{ success: boolean }> =>
    api.patch(`/processes/instances/${instanceId}/field-values`, { field_values: fieldValues }).then(r => r.data),

  // Instance Stage / Sort (Kanban)
  updateInstanceStage: (id: number, stageKey: string): Promise<{ success: boolean }> =>
    api.patch(`/processes/instances/${id}/stage`, { stage_key: stageKey }).then(r => r.data),
  updateInstanceDescription: (id: number, description: string): Promise<{ success: boolean }> =>
    api.patch(`/processes/instances/${id}/description`, { description }).then(r => r.data),

  // Manual Tasks
  createManualTask: (instanceId: number, data: { title: string; description?: string; assigned_to?: number; due_date?: string }): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/instances/${instanceId}/manual-tasks`, data).then(r => r.data),

  // Instance Files
  getInstanceFiles: (instanceId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/processes/instances/${instanceId}/files`).then(r => r.data),
  uploadInstanceFile: (instanceId: number, formData: FormData): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/instances/${instanceId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  deleteInstanceFile: (instanceId: number, fileId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/instances/${instanceId}/files/${fileId}`).then(r => r.data),
  downloadInstanceFile: (instanceId: number, fileId: number) =>
    api.get(`/processes/instances/${instanceId}/files/${fileId}/download`, { responseType: 'blob' }),

  // Instance Comments
  getInstanceComments: (instanceId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/processes/instances/${instanceId}/comments`).then(r => r.data),
  createInstanceComment: (instanceId: number, content: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/instances/${instanceId}/comments`, { content }).then(r => r.data),
  deleteInstanceComment: (instanceId: number, commentId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/instances/${instanceId}/comments/${commentId}`).then(r => r.data),

  // Marketing Instances
  getMarketingInstances: (templateId?: number): Promise<{ success: boolean; data: any[] }> =>
    api.get('/processes/marketing-instances', { params: templateId ? { template_id: templateId } : {} }).then(r => r.data),

  // Template Export/Import
  exportTemplate: (templateId: number): Promise<{ success: boolean; data: any }> =>
    api.get(`/processes/templates/${templateId}/export`).then(r => r.data),
  importTemplate: (payload: any): Promise<{ success: boolean; data: any }> =>
    api.post('/processes/templates/import', { payload }).then(r => r.data),

  // Webhook API Keys
  getWebhookKeys: (templateId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/processes/templates/${templateId}/webhook-keys`).then(r => r.data),
  createWebhookKey: (templateId: number, label?: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/processes/templates/${templateId}/webhook-keys`, { label }).then(r => r.data),
  revokeWebhookKey: (keyId: number): Promise<{ success: boolean }> =>
    api.delete(`/processes/webhook-keys/${keyId}`).then(r => r.data),

  // Portal Tokens
};

// ── Marketing Content Creation ────────────────────────────────────────


// ─── Inbox API ────────────────────────────────────────────────────────
// Factory: createInboxApi('shared') for Help Desk, createInboxApi('personal') for Personal Inbox.
// The default `inboxApi` export is the shared (Help Desk) version for backward compatibility.

export type AccountType = 'shared' | 'personal';

function atParam(at: AccountType): { account_type: string } {
  return { account_type: at };
}

export function createInboxApi(accountType: AccountType = 'shared') {
  const at = accountType;
  return {
    // v54: enriched response with granted_scopes diff. Old fields kept.
    getStatus: (): Promise<{ success: boolean; data: {
      connected: boolean;
      email: string | null;
      syncStartedAt: string | null;
      watchExpiry: string | null;
      accountType: string | null;
      granted_scopes: string | null;
      expected_scopes: string[];
      missing_scopes: string[];
      scope_tracking_unknown: boolean;
      expires_at: string | null;
      has_refresh_token: boolean;
    } }> =>
      api.get('/inbox/status', { params: atParam(at) }).then(r => r.data),
    connect: (): Promise<{ success: boolean; data: { url: string } }> =>
      api.get('/inbox/connect', { params: atParam(at) }).then(r => r.data),
    disconnect: (): Promise<{ success: boolean }> =>
      api.delete('/inbox/disconnect', { params: atParam(at) }).then(r => r.data),
    sync: (): Promise<{ success: boolean; data: { synced: number } }> =>
      api.post('/inbox/sync', atParam(at)).then(r => r.data),

    getThreads: (params: { status?: string; folder_id?: number; label_id?: number; assigned_to?: number; search?: string; search_from?: string; search_to?: string; search_domain?: string; search_subject?: string; search_body?: string; search_has_attachment?: boolean; page?: number; limit?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
      api.get('/inbox/threads', { params: { ...params, ...atParam(at) } }).then(r => r.data),
    getThread: (id: number): Promise<{ success: boolean; data: { thread: any; messages: any[]; notes: any[]; ticket: any; receiptStats?: Record<string, any> } }> =>
      api.get(`/inbox/threads/${id}`).then(r => r.data),
    updateThread: (id: number, data: any): Promise<{ success: boolean; data: any }> =>
      api.patch(`/inbox/threads/${id}`, data).then(r => r.data),
    archiveThread: (id: number): Promise<{ success: boolean }> =>
      api.post(`/inbox/threads/${id}/archive`).then(r => r.data),
    reopenThread: (id: number): Promise<{ success: boolean }> =>
      api.post(`/inbox/threads/${id}/reopen`).then(r => r.data),
    snoozeThread: (id: number, until: string): Promise<{ success: boolean }> =>
      api.post(`/inbox/threads/${id}/snooze`, { until }).then(r => r.data),
    bulkArchive: (threadIds: number[]): Promise<{ success: boolean }> =>
      api.post('/inbox/threads/bulk-archive', { thread_ids: threadIds }).then(r => r.data),

    shareToHelpdesk: (threadId: number, data?: { title?: string; description?: string }): Promise<{ success: boolean; data: { thread: any; ticket: any } }> =>
      api.post(`/inbox/threads/${threadId}/share-to-helpdesk`, data || {}).then(r => r.data),

    // `templateAttachmentIds` carries the attachments of an APPLIED email
    // template. They stay server-side ids rather than downloaded Files so a
    // 10MB PDF doesn't cross the wire twice — and so a background-prepared
    // draft, which has no browser to hold a File, can carry one at all.
    // Sent in both branches: multer puts multipart text fields on req.body,
    // and skips non-multipart requests entirely, so the JSON branch works too.
    reply: (threadId: number, data: { to?: string[]; cc?: string[]; bcc?: string[]; htmlBody: string; attachments?: File[]; templateAttachmentIds?: number[] }): Promise<{ success: boolean; data: { messageId: string } }> => {
      if (data.attachments?.length) {
        const fd = new FormData();
        fd.append('to', JSON.stringify(data.to || []));
        if (data.cc) fd.append('cc', JSON.stringify(data.cc));
        if (data.bcc) fd.append('bcc', JSON.stringify(data.bcc));
        fd.append('htmlBody', data.htmlBody);
        if (data.templateAttachmentIds?.length) fd.append('template_attachment_ids', JSON.stringify(data.templateAttachmentIds));
        data.attachments.forEach(f => fd.append('attachments', f));
        return api.post(`/inbox/threads/${threadId}/reply`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
      }
      const { attachments: _, templateAttachmentIds, ...rest } = data;
      return api.post(`/inbox/threads/${threadId}/reply`, { ...rest, ...(templateAttachmentIds?.length ? { template_attachment_ids: templateAttachmentIds } : {}) }).then(r => r.data);
    },
    forward: (threadId: number, data: { to: string[]; cc?: string[]; htmlBody: string; attachments?: File[]; templateAttachmentIds?: number[] }): Promise<{ success: boolean; data: { messageId: string } }> => {
      if (data.attachments?.length) {
        const fd = new FormData();
        fd.append('to', JSON.stringify(data.to));
        if (data.cc) fd.append('cc', JSON.stringify(data.cc));
        fd.append('htmlBody', data.htmlBody);
        if (data.templateAttachmentIds?.length) fd.append('template_attachment_ids', JSON.stringify(data.templateAttachmentIds));
        data.attachments.forEach(f => fd.append('attachments', f));
        return api.post(`/inbox/threads/${threadId}/forward`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
      }
      const { attachments: _, templateAttachmentIds, ...rest } = data;
      return api.post(`/inbox/threads/${threadId}/forward`, { ...rest, ...(templateAttachmentIds?.length ? { template_attachment_ids: templateAttachmentIds } : {}) }).then(r => r.data);
    },
    // Phase 7c c2.6 (state.md Entry 70): optional `fromAccountId` lets the
    // task composer's "From" dropdown pick the sending inbox. Omitted by
    // every existing caller → unchanged behavior.
    compose: (data: { to: string[]; cc?: string[]; bcc?: string[]; subject: string; htmlBody: string; attachments?: File[]; templateAttachmentIds?: number[]; fromAccountId?: number }): Promise<{ success: boolean; data: { messageId: string } }> => {
      if (data.attachments?.length) {
        const fd = new FormData();
        fd.append('to', JSON.stringify(data.to));
        if (data.cc) fd.append('cc', JSON.stringify(data.cc));
        if (data.bcc) fd.append('bcc', JSON.stringify(data.bcc));
        fd.append('subject', data.subject);
        fd.append('htmlBody', data.htmlBody);
        if (data.fromAccountId != null) fd.append('fromAccountId', String(data.fromAccountId));
        if (data.templateAttachmentIds?.length) fd.append('template_attachment_ids', JSON.stringify(data.templateAttachmentIds));
        const extra = atParam(at);
        if (extra.account_type) fd.append('account_type', extra.account_type);
        data.attachments.forEach(f => fd.append('attachments', f));
        return api.post('/inbox/compose', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
      }
      const { attachments: _, templateAttachmentIds, ...rest } = data;
      return api.post('/inbox/compose', {
        ...rest,
        ...(templateAttachmentIds?.length ? { template_attachment_ids: templateAttachmentIds } : {}),
        ...atParam(at),
      }).then(r => r.data);
    },
    // Phase 7c c2.6 (state.md Entry 70): accounts the current user may send
    // AS (their connected personal inbox + accessible shared inboxes).
    senderAccounts: (): Promise<{ success: boolean; data: Array<{ id: number; email: string; account_type: 'personal' | 'shared'; connected: boolean }> }> =>
      api.get('/inbox/sender-accounts').then(r => r.data),
    saveDraft: (data: { to?: string[]; cc?: string[]; bcc?: string[]; subject?: string; htmlBody?: string; threadId?: string }): Promise<{ success: boolean; data: { draftId: string } }> =>
      api.post('/inbox/drafts', { ...data, ...atParam(at) }).then(r => r.data),
    getDrafts: (): Promise<{ success: boolean; data: any[] }> =>
      api.get('/inbox/drafts', { params: atParam(at) }).then(r => r.data),
    getDraft: (draftId: string): Promise<{ success: boolean; data: any }> =>
      api.get(`/inbox/drafts/${draftId}`, { params: atParam(at) }).then(r => r.data),
    deleteDraft: (draftId: string): Promise<{ success: boolean }> =>
      api.delete(`/inbox/drafts/${draftId}`, { params: atParam(at) }).then(r => r.data),
    deleteDrafts: async (draftIds: string[]): Promise<{ success: boolean; deleted: number }> => {
      let deleted = 0;
      for (const id of draftIds) {
        try { await api.delete(`/inbox/drafts/${id}`, { params: atParam(at) }); deleted++; } catch {}
      }
      return { success: true, deleted };
    },
    updateDraft: (draftId: string, data: { to?: string[]; cc?: string[]; bcc?: string[]; subject?: string; htmlBody?: string; threadId?: string }): Promise<{ success: boolean; data: { draftId: string } }> =>
      api.put(`/inbox/drafts/${draftId}`, { ...data, ...atParam(at) }).then(r => r.data),
    sendDraft: (draftId: string): Promise<{ success: boolean }> =>
      api.post(`/inbox/drafts/${draftId}/send`, atParam(at)).then(r => r.data),

    getScheduled: (): Promise<{ success: boolean; data: any[] }> =>
      api.get('/inbox/scheduled', { params: atParam(at) }).then(r => r.data),

    getAttachment: (messageId: string, attachmentId: string, mime?: string, filename?: string): Promise<Blob> =>
      api.get(`/inbox/messages/${messageId}/attachment/${attachmentId}`, {
        responseType: 'blob',
        params: { mime: mime || 'application/octet-stream', filename: filename || 'attachment' },
      }).then(r => r.data),

    getAttachmentUrl: (messageId: string, attachmentId: string, mime?: string, filename?: string): string => {
      const base = (process.env.REACT_APP_API_URL || '/api');
      const params = new URLSearchParams({ mime: mime || 'application/octet-stream', filename: filename || 'attachment' });
      return `${base}/inbox/messages/${messageId}/attachment/${attachmentId}?${params.toString()}`;
    },

    addNote: (threadId: number, content: string, mentions?: number[]): Promise<{ success: boolean; data: { id: number } }> =>
      api.post(`/inbox/threads/${threadId}/notes`, { content, mentions }).then(r => r.data),
    updateNote: (noteId: number, content: string): Promise<{ success: boolean }> =>
      api.put(`/inbox/notes/${noteId}`, { content }).then(r => r.data),

    createTicket: (threadId: number, data: any): Promise<{ success: boolean; data: any }> =>
      api.post(`/inbox/threads/${threadId}/ticket`, data).then(r => r.data),
    getTickets: (params: {
      status?: string;
      assigned_to?: number;
      client_card_id?: string;
      ticket_type?: 'internal' | 'external_support';
      support_provider?: string | string[]; // W3 — single value or array
      page?: number;
      limit?: number;
      // W Chunk B — decay view extensions.
      stale_days?: number;
      sort?: 'last_touched_at';
      // W Chunk B+ — server-side search across id + title + description.
      search?: string;
    }): Promise<{ success: boolean; data: any[]; total: number }> =>
      api.get('/inbox/tickets', { params }).then(r => r.data),
    // Per-bucket counts for the ticket SavedViewsBar tab badges. Each bucket's
    // total is computed server-side (COUNT(*), not page-limited), so the
    // My/Active/Closed/All tabs each show their own number instead of all
    // mirroring the active view's loaded slice.
    getTicketCounts: (): Promise<{ success: boolean; data: { my: number; active: number; closed: number; all: number } }> =>
      api.get('/inbox/tickets/counts').then(r => r.data),
    updateTicket: (id: number, data: any): Promise<{ success: boolean; data: any }> =>
      api.patch(`/inbox/tickets/${id}`, data).then(r => r.data),
    ticketComments: {
      list: (ticketId: number): Promise<{ success: boolean; data: any[] }> =>
        api.get(`/inbox/tickets/${ticketId}/comments`).then(r => r.data),
      create: (ticketId: number, content_html: string): Promise<{ success: boolean; data: any }> =>
        api.post(`/inbox/tickets/${ticketId}/comments`, { content_html }).then(r => r.data),
      update: (ticketId: number, commentId: number, content_html: string): Promise<{ success: boolean; data: any }> =>
        api.patch(`/inbox/tickets/${ticketId}/comments/${commentId}`, { content_html }).then(r => r.data),
      delete: (ticketId: number, commentId: number): Promise<{ success: boolean }> =>
        api.delete(`/inbox/tickets/${ticketId}/comments/${commentId}`).then(r => r.data),
    },
    // W Chunk A2a — ticket attachments (disk-backed storage). The upload
    // uses multipart form-data with field name "file". `downloadUrl` returns
    // a string usable as <a href> or <img src> — the GET route requires auth
    // via the same cookie/header that carries the rest of the inbox client.
    ticketAttachments: {
      list: (ticketId: number): Promise<{ success: boolean; data: any[] }> =>
        api.get(`/inbox/tickets/${ticketId}/attachments`).then(r => r.data),
      upload: (ticketId: number, file: File): Promise<{ success: boolean; data: any }> => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/inbox/tickets/${ticketId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }).then(r => r.data);
      },
      // Pure URL builder — no fetch. Use as <a href> or <img src>.
      downloadUrl: (ticketId: number, attachmentId: number): string =>
        `${api.defaults.baseURL || ''}/inbox/tickets/${ticketId}/attachments/${attachmentId}/download`,
      delete: (ticketId: number, attachmentId: number): Promise<{ success: boolean }> =>
        api.delete(`/inbox/tickets/${ticketId}/attachments/${attachmentId}`).then(r => r.data),
    },
    // W Chunk A2b — ticket linked threads (M:N with inbox_threads). The
    // backend dedupes (ticket, thread) pairs and emits a `thread_linked`
    // ticket_activity row via B's recordActivity helper.
    ticketThreads: {
      list: (ticketId: number): Promise<{ success: boolean; data: any[] }> =>
        api.get(`/inbox/tickets/${ticketId}/threads`).then(r => r.data),
      link: (ticketId: number, threadId: number, role?: string): Promise<{ success: boolean; data: any }> =>
        api.post(`/inbox/tickets/${ticketId}/threads`, { thread_id: threadId, role }).then(r => r.data),
      unlink: (ticketId: number, threadId: number): Promise<{ success: boolean }> =>
        api.delete(`/inbox/tickets/${ticketId}/threads/${threadId}`).then(r => r.data),
    },
    // W Chunk A3 — ticket-scoped todos. Wraps the canonical todos API
    // (each ticket-scoped todo also lives in the user's main /todos
    // view via the existing source_type='ticket' + source_id pattern).
    // W Chunk C — manual Send CSAT from the HubDialog action bar.
    sendCsat: (ticketId: number, force?: boolean): Promise<{ success: boolean; data: any }> =>
      api.post(`/inbox/tickets/${ticketId}/csat/send`, { force: !!force }).then(r => r.data),
    ticketTodos: {
      list: (ticketId: number): Promise<{ success: boolean; data: any[] }> =>
        api.get(`/inbox/tickets/${ticketId}/todos`).then(r => r.data),
      create: (
        ticketId: number,
        data: { title: string; description?: string; priority?: string; assigned_to?: number; due_date?: string },
      ): Promise<{ success: boolean; data: any }> =>
        api.post(`/inbox/tickets/${ticketId}/todos`, data).then(r => r.data),
      // A3.5 — toggle completion. Server derives the new state from current
      // (no body required).
      toggle: (ticketId: number, todoId: number): Promise<{ success: boolean; data: any }> =>
        api.patch(`/inbox/tickets/${ticketId}/todos/${todoId}`, {}).then(r => r.data),
    },
    // W Chunk A2b — ticket contacts (additional stakeholders beyond the
    // single assignee). One role per (ticket, contact) pair. Role taxonomy
    // matches the W1 CHECK enum: main_poc / cc / escalation /
    // external_assignee / other. add + remove emit activity events; role
    // tweaks via PATCH do not.
    ticketContacts: {
      list: (ticketId: number): Promise<{ success: boolean; data: any[] }> =>
        api.get(`/inbox/tickets/${ticketId}/contacts`).then(r => r.data),
      add: (ticketId: number, contactId: number, role: string): Promise<{ success: boolean; data: any }> =>
        api.post(`/inbox/tickets/${ticketId}/contacts`, { contact_id: contactId, role }).then(r => r.data),
      updateRole: (ticketId: number, contactId: number, role: string): Promise<{ success: boolean; data: any }> =>
        api.patch(`/inbox/tickets/${ticketId}/contacts/${contactId}`, { role }).then(r => r.data),
      remove: (ticketId: number, contactId: number): Promise<{ success: boolean }> =>
        api.delete(`/inbox/tickets/${ticketId}/contacts/${contactId}`).then(r => r.data),
    },
    // Helper for TemplateRichTextEditor's `onImageUpload` prop — uploads a
    // pasted/dropped image into ticket-attachment storage and returns the
    // download URL that the editor inlines as <img src=…/>. Reuses `upload`.
    uploadInlineImageForTicket: async (ticketId: number, file: File): Promise<string> => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/inbox/tickets/${ticketId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = res.data?.data;
      if (!created?.id) throw new Error('Inline image upload failed: no attachment id returned');
      return `${api.defaults.baseURL || ''}/inbox/tickets/${ticketId}/attachments/${created.id}/download`;
    },
    getTicketById: (id: number): Promise<{ success: boolean; data: any }> =>
      api.get(`/inbox/tickets/${id}`).then(r => r.data),
    closeTicket: (id: number): Promise<{ success: boolean; data: any }> =>
      api.post(`/inbox/tickets/${id}/close`).then(r => r.data),
    deleteTicket: (id: number): Promise<{ success: boolean }> =>
      api.delete(`/inbox/tickets/${id}`).then(r => r.data),
    createStandaloneTicket: (data: { title: string; description?: string; client_card_id?: string; priority?: string; assigned_to?: number }): Promise<{ success: boolean; data: any }> =>
      api.post('/inbox/tickets/create', data).then(r => r.data),
    bulkAction: (ids: number[], action: 'close' | 'update', data?: Record<string, any>): Promise<{ success: boolean; data: { updated: number; errors: string[] } }> =>
      api.post('/inbox/tickets/bulk', { ids, action, data }).then(r => r.data),
    getTicketActivity: (id: number): Promise<{ success: boolean; data: any[] }> =>
      api.get(`/inbox/tickets/${id}/activity`).then(r => r.data),

    aiDraft: (threadId: number): Promise<{ success: boolean; data: { draft: string } }> =>
      api.post(`/inbox/threads/${threadId}/ai-draft`).then(r => r.data),
    aiTicket: (threadId: number): Promise<{ success: boolean; data: { title: string; description: string } }> =>
      api.post(`/inbox/threads/${threadId}/ai-ticket`).then(r => r.data),

    startTicketTimer: (ticketId: number): Promise<{ success: boolean; data: any }> =>
      api.post(`/inbox/tickets/${ticketId}/start-timer`).then(r => r.data),

    getTemplates: (): Promise<{ success: boolean; data: Array<{ id: number; name: string; subject?: string; body: string; category?: string }> }> =>
      api.get('/inbox/templates').then(r => r.data),
    createTemplate: (data: { name: string; subject?: string; body: string; category?: string }): Promise<{ success: boolean; data: { id: number } }> =>
      api.post('/inbox/templates', data).then(r => r.data),
    updateTemplate: (id: number, data: { name?: string; subject?: string; body?: string; category?: string }): Promise<{ success: boolean }> =>
      api.put(`/inbox/templates/${id}`, data).then(r => r.data),
    deleteTemplate: (id: number): Promise<{ success: boolean }> =>
      api.delete(`/inbox/templates/${id}`).then(r => r.data),

    mergeThread: (sourceId: number, targetId: number): Promise<{ success: boolean; data: { merged: number } }> =>
      api.post(`/inbox/threads/${sourceId}/merge`, { target_thread_id: targetId }).then(r => r.data),

    getFolders: (folderType?: 'folder' | 'label'): Promise<{ success: boolean; data: any[] }> =>
      api.get('/inbox/folders', { params: { ...atParam(at), ...(folderType ? { folder_type: folderType } : {}) } }).then(r => r.data),
    createFolder: (name: string, color?: string, parentId?: number, folderType?: 'folder' | 'label'): Promise<{ success: boolean; data: { id: number } }> =>
      api.post('/inbox/folders', { name, color, parent_id: parentId, folder_type: folderType || 'label', ...atParam(at) }).then(r => r.data),
    deleteFolder: (id: number): Promise<{ success: boolean }> =>
      api.delete(`/inbox/folders/${id}`).then(r => r.data),

    getSettings: (): Promise<{ success: boolean; data: Record<string, string> }> =>
      api.get('/inbox/settings', { params: atParam(at) }).then(r => r.data),
    updateSettings: (settings: Record<string, string>): Promise<{ success: boolean }> =>
      api.put('/inbox/settings', { ...settings, ...atParam(at) }).then(r => r.data),

    getFilterRules: (): Promise<{ success: boolean; data: any[] }> =>
      api.get('/inbox/filter-rules', { params: atParam(at) }).then(r => r.data),
    createFilterRule: (data: any): Promise<{ success: boolean; data: { id: number } }> =>
      api.post('/inbox/filter-rules', { ...data, ...atParam(at) }).then(r => r.data),
    updateFilterRule: (id: number, data: any): Promise<{ success: boolean }> =>
      api.put(`/inbox/filter-rules/${id}`, { ...data, ...atParam(at) }).then(r => r.data),
    deleteFilterRule: (id: number): Promise<{ success: boolean }> =>
      api.delete(`/inbox/filter-rules/${id}`, { params: atParam(at) }).then(r => r.data),

    getClients: (): Promise<{ success: boolean; data: Array<{ card_id: string; title: string }> }> =>
      api.get('/inbox/clients').then(r => r.data),

    getClientRouting: (): Promise<{ success: boolean; data: any[] }> =>
      api.get('/inbox/client-routing').then(r => r.data),
    getClientsWithTickets: (): Promise<{ success: boolean; data: ClientWithTickets[] }> =>
      api.get('/inbox/clients-with-tickets', { params: atParam(at) }).then(r => r.data),

    getClientThreads: (cardId: string, status?: string): Promise<{ success: boolean; data: any[] }> =>
      api.get(`/inbox/clients/${cardId}/threads`, { params: { status, ...atParam(at) } }).then(r => r.data),

    applyClientDomainRetroactive: (clientCardId: string, type: string, value: string): Promise<{ success: boolean; data: { affected: number } }> =>
      api.post('/inbox/apply-client-domain', { client_card_id: clientCardId, type, value, ...atParam(at) }).then(r => r.data),

    applyFilterRuleRetroactive: (ruleId: number): Promise<{ success: boolean; data: { affected: number } }> =>
      api.post(`/inbox/apply-filter-rule/${ruleId}`, atParam(at)).then(r => r.data),
  };
}

export type InboxApiType = ReturnType<typeof createInboxApi>;

// Default shared (Help Desk) instance for backward compatibility
export const inboxApi = createInboxApi('shared');

// W3 demolition: the standalone `externalSupportApi` client (which backed the
// External Support help-desk page + the standalone external_support_items
// system) is gone. The 12 standalone routes + service + 2 frontend
// components were removed. External Support is now a ticket attribute:
//   - settingsApi.supportProviders.* manages the dropdown values
//   - inboxApi.convertToExternalSupport(ticketId, fields) flips ticket_type
//   - inboxApi.getTickets({ticket_type, support_provider}) filters/queries
//
// "Convert to External Support" callers should use
// `inboxApi.convertToExternalSupport(ticketId, {...})` (added below).

// ── Todos API ────────────────────────────────────────────────────────

export const todosApi = {
  getTodos: (filters?: Record<string, any>): Promise<{ success: boolean; data: TodoItem[]; total: number }> =>
    api.get('/todos', { params: filters }).then(r => r.data),

  getTodo: (id: number): Promise<{ success: boolean; data: TodoItem & { activity: TodoActivityEntry[] } }> =>
    api.get(`/todos/${id}`).then(r => r.data),

  createTodo: (data: Partial<TodoItem>): Promise<{ success: boolean; data: TodoItem }> =>
    api.post('/todos', data).then(r => r.data),

  updateTodo: (id: number, data: Partial<TodoItem>): Promise<{ success: boolean; data: TodoItem }> =>
    api.patch(`/todos/${id}`, data).then(r => r.data),

  completeTodo: (id: number, fieldValues?: any[]): Promise<{ success: boolean; data: TodoItem }> =>
    api.post(`/todos/${id}/complete`, { fieldValues }).then(r => r.data),

  reopenTodo: (id: number): Promise<{ success: boolean; data: TodoItem }> =>
    api.post(`/todos/${id}/reopen`).then(r => r.data),

  deleteTodo: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/todos/${id}`).then(r => r.data),

  convertToProject: (id: number): Promise<{ success: boolean; data: any }> =>
    api.post(`/todos/${id}/convert-to-project`).then(r => r.data),

  bulkAction: (ids: number[], action: 'complete' | 'reopen' | 'delete' | 'update', data?: Record<string, any>): Promise<{ success: boolean; data: { updated: number; errors: string[] } }> =>
    api.post('/todos/bulk', { ids, action, data }).then(r => r.data),

  getStatuses: (section?: string): Promise<{ success: boolean; data: TodoStatus[] | Record<string, TodoStatus[]> }> =>
    api.get('/todos/statuses/all', { params: { section } }).then(r => r.data),

  getSavedViews: (): Promise<{ success: boolean; data: TodoSavedView[] }> =>
    api.get('/todos/saved-views/all').then(r => r.data),

  createSavedView: (data: { name: string; config_json: string; is_shared?: boolean }): Promise<{ success: boolean; data: TodoSavedView }> =>
    api.post('/todos/saved-views', data).then(r => r.data),

};

// ── Projects API ─────────────────────────────────────────────────────

export const projectsApi = {
  getProjects: (filters?: Record<string, any>): Promise<{ success: boolean; data: Project[]; total: number }> =>
    api.get('/projects', { params: filters }).then(r => r.data),

  getProject: (id: number): Promise<{ success: boolean; data: Project & { activity: ProjectActivityEntry[] } }> =>
    api.get(`/projects/${id}`).then(r => r.data),

  createProject: (data: Partial<Project>): Promise<{ success: boolean; data: Project }> =>
    api.post('/projects', data).then(r => r.data),

  updateProject: (id: number, data: Partial<Project>): Promise<{ success: boolean; data: Project }> =>
    api.patch(`/projects/${id}`, data).then(r => r.data),

  deleteProject: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/projects/${id}`).then(r => r.data),

  getCount: (): Promise<{ success: boolean; data: { active: number; completed: number; total: number } }> =>
    api.get('/projects/count').then(r => r.data),

  getFiles: (projectId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/projects/${projectId}/files`).then(r => r.data),

  uploadFile: (projectId: number, file: File): Promise<{ success: boolean; data: any }> => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/projects/${projectId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  deleteFile: (fileId: number): Promise<{ success: boolean }> =>
    api.delete(`/projects/files/${fileId}`).then(r => r.data),

  getComments: (projectId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/projects/${projectId}/comments`).then(r => r.data),

  addComment: (projectId: number, content: string): Promise<{ success: boolean; data: any }> =>
    api.post(`/projects/${projectId}/comments`, { content }).then(r => r.data),

};

// ── Notifications API ───────────────────────────────────────────────


// ── App Requests (Feature / Bug) ────────────────────────────────────


// ── Flowcharts API ────────────────────────────────────────────────────

export const flowchartsApi = {
  // Projects
  getProjects: (filter?: { client_card_id?: string; status?: string }): Promise<{ success: boolean; data: FlowchartProject[] }> =>
    api.get('/flowcharts/projects', { params: filter }).then(r => r.data),
  createProject: (data: { name: string; description?: string; client_card_id?: string; fathom_meeting_id?: number }): Promise<{ success: boolean; data: FlowchartProject }> =>
    api.post('/flowcharts/projects', data).then(r => r.data),
  updateProject: (id: number, data: Partial<FlowchartProject>): Promise<{ success: boolean; data: FlowchartProject }> =>
    api.put(`/flowcharts/projects/${id}`, data).then(r => r.data),
  deleteProject: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/flowcharts/projects/${id}`).then(r => r.data),

  // Files
};


// ── CRM API ──────────────────────────────────────────────────────────

export interface EmailSuggestion {
  contact_id: number;
  email: string;
  name: string;
  company_name: string | null;
  client_name: string | null;
  contact_type: string | null;
  role: string | null;
  avatar_url: string | null;
}

export const crmApi = {
  searchEmails: (q: string, limit = 15): Promise<{ success: boolean; data: EmailSuggestion[] }> =>
    api.get('/crm/email-suggestions', { params: { q, limit } }).then(r => r.data),

  getContacts: (params?: { search?: string; client_card_id?: string; owner_user_id?: number; tag?: string; source?: string; page?: number; limit?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/crm/contacts', { params }).then(r => r.data),

  getContact: (id: number): Promise<{ success: boolean; data: any }> =>
    api.get(`/crm/contacts/${id}`).then(r => r.data),

  createContact: (data: any): Promise<{ success: boolean; data: any }> =>
    api.post('/crm/contacts', data).then(r => r.data),

  updateContact: (id: number, data: any): Promise<{ success: boolean; data: any }> =>
    api.put(`/crm/contacts/${id}`, data).then(r => r.data),

  deleteContact: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/crm/contacts/${id}`).then(r => r.data),

  addContactEmail: (contactId: number, data: { email: string; label?: string; is_primary?: boolean }): Promise<{ success: boolean; data: any }> =>
    api.post(`/crm/contacts/${contactId}/emails`, data).then(r => r.data),

  addContactPhone: (contactId: number, data: { phone_number: string; label?: string; is_primary?: boolean; can_sms?: boolean }): Promise<{ success: boolean; data: any }> =>
    api.post(`/crm/contacts/${contactId}/phones`, data).then(r => r.data),

};


// ── Communications API ───────────────────────────────────────────────

export const communicationsApi = {
  // Voice
  getVoiceToken: (): Promise<{ success: boolean; data: { token: string; identity: string } }> =>
    api.post('/communications/voice/token').then(r => r.data),

  initiateCall: (data: { to_number: string; contact_id?: number; client_card_id?: string }): Promise<{ success: boolean; data: { call_id: number; from_number: string } }> =>
    api.post('/communications/calls/outbound', data).then(r => r.data),

  getCallLogs: (params?: { contact_id?: number; limit?: number; offset?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/communications/calls', { params }).then(r => ({ success: r.data.success, ...r.data.data })),

  updateCallLog: (id: number, data: any): Promise<{ success: boolean; data: any }> =>
    api.patch(`/communications/calls/${id}`, data).then(r => r.data),

  // SMS
  sendSms: (data: { to_number: string; body: string; contact_id?: number; client_card_id?: string; from_number?: string; media_urls?: string[] }): Promise<{ success: boolean; data: { message_id: number; thread_id: number; twilio_sid: string } }> =>
    api.post('/communications/sms/send', data).then(r => r.data),

  getSmsThreads: (params?: { contact_id?: number; limit?: number; offset?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/communications/sms/threads', { params }).then(r => ({ success: r.data.success, ...r.data.data })),

  getSmsThread: (id: number): Promise<{ success: boolean; data: { thread: any; messages: any[] } }> =>
    api.get(`/communications/sms/threads/${id}`).then(r => r.data),

  sendInThread: (threadId: number, body: string): Promise<{ success: boolean; data: { message_id: number; twilio_sid: string } }> =>
    api.post(`/communications/sms/threads/${threadId}/send`, { body }).then(r => r.data),

  // Phone Numbers
  getPhoneNumbers: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/communications/numbers').then(r => r.data),

  getCommSettings: (): Promise<{ success: boolean; data: any }> =>
    api.get('/communications/settings').then(r => r.data),

  saveCommSettings: (data: any): Promise<{ success: boolean; data: any }> =>
    api.put('/communications/settings', data).then(r => r.data),

  getVoiceProfiles: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/communications/ai-calls/profiles').then(r => r.data),

  testAiCall: (data: { phone: string; voiceProfileId: number; instructions?: string }): Promise<{ success: boolean; data: any }> =>
    api.post('/communications/ai-calls/test', data).then(r => r.data),

  listAiCalls: (filters?: { origin_type?: string; status?: string; lead_id?: number; contact_id?: number; deal_id?: number; limit?: number; offset?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/communications/ai-calls', { params: filters }).then(r => ({ success: r.data.success, ...r.data.data })),

};


export interface PhoneRule {
  id: number;
  phone_number_id: number | null;
  phone_number?: string;
  friendly_name?: string | null;
  name: string;
  trigger_type: 'call' | 'sms' | 'both';
  condition_type: 'all' | 'unknown_number' | 'known_contact' | 'known_lead' | 'from_number_contains';
  condition_value: string | null;
  action_type: 'route_assigned' | 'assign_user' | 'voicemail' | 'skip';
  action_value: string | null;
  priority: number;
  is_active: number;
}

export interface ScheduledSms {
  id: number;
  from_number: string;
  to_number: string;
  body: string;
  scheduled_for: string;
  status: string;
  error_message: string | null;
  contact_name?: string | null;
  lead_contact_name?: string | null;
  lead_company_name?: string | null;
}

export const phonesApi = {
  getNumbers: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/phones/numbers').then(r => r.data),

  updateNumberAccess: (id: number, userIds: number[]): Promise<{ success: boolean; data: any }> =>
    api.put(`/phones/numbers/${id}/access`, { user_ids: userIds }).then(r => r.data),

  scheduleSms: (data: { to_number: string; body: string; scheduled_for: string; from_number?: string; phone_number_id?: number; contact_id?: number; lead_id?: number; client_card_id?: string }): Promise<{ success: boolean; data: ScheduledSms }> =>
    api.post('/phones/sms/schedule', data).then(r => r.data),

  getScheduledSms: (status?: string): Promise<{ success: boolean; data: ScheduledSms[] }> =>
    api.get('/phones/sms/scheduled', { params: status ? { status } : {} }).then(r => r.data),

  getRules: (): Promise<{ success: boolean; data: PhoneRule[] }> =>
    api.get('/phones/rules').then(r => r.data),

  createRule: (data: Partial<PhoneRule>): Promise<{ success: boolean; data: PhoneRule }> =>
    api.post('/phones/rules', data).then(r => r.data),

  updateRule: (id: number, data: Partial<PhoneRule>): Promise<{ success: boolean; data: PhoneRule }> =>
    api.put(`/phones/rules/${id}`, data).then(r => r.data),

  deleteRule: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/phones/rules/${id}`).then(r => r.data),
};


// ── Associations API ────────────────────────────────────────────────
export const associationsApi = {
  getAssociations: (objectType: string, objectId: string, targetType?: string) =>
    api.get('/associations', { params: { object_type: objectType, object_id: objectId, target_type: targetType } }),
};

// ── Custom Properties API ───────────────────────────────────────────
export const customPropertiesApi = {
  getDefinitions: (objectType: string) =>
    api.get('/custom-properties/definitions', { params: { object_type: objectType } }),
  getValues: (objectType: string, objectId: string) =>
    api.get('/custom-properties/values', { params: { object_type: objectType, object_id: objectId } }),
  setValue: (data: { object_type: string; object_id: string; property_id: number; value: string | null }) =>
    api.put('/custom-properties/values', data),
  bulkSetValues: (data: { object_type: string; object_id: string; values: Array<{ propertyId: number; value: string | null }> }) =>
    api.put('/custom-properties/values/bulk', data),
};

// ── Deals API ───────────────────────────────────────────────────────
export const dealsApi = {
  list: (filters?: { pipeline_id?: number; status?: string; owner_id?: number; page?: number; limit?: number }) =>
    api.get('/deals', { params: filters }),
  get: (id: number) => api.get(`/deals/${id}`),
  create: (data: Record<string, any>) => api.post('/deals', data),
  update: (id: number, data: Record<string, any>) => api.put(`/deals/${id}`, data),
  changeStage: (id: number, stage: string) => api.patch(`/deals/${id}/stage`, { stage }),
  getContacts: (dealId: number) => api.get(`/deals/${dealId}/contacts`).then(r => r.data),
  addContact: (dealId: number, contactId: number, role?: string) =>
    api.post(`/deals/${dealId}/contacts`, { contact_id: contactId, role }).then(r => r.data),
  removeContact: (dealId: number, contactId: number) =>
    api.delete(`/deals/${dealId}/contacts/${contactId}`).then(r => r.data),
  getFiles: (dealId: number) => api.get(`/deals/${dealId}/files`).then(r => r.data),
  uploadFile: (dealId: number, data: { file_name: string; file_path: string; file_type?: string; file_size?: number }) =>
    api.post(`/deals/${dealId}/files`, data).then(r => r.data),
  deleteFile: (dealId: number, fileId: number) =>
    api.delete(`/deals/${dealId}/files/${fileId}`).then(r => r.data),
};

// ── Activities API ──────────────────────────────────────────────────
export const activitiesApi = {
  list: (filters: { contact_id?: number; lead_id?: number; company_card_id?: string; deal_id?: number; activity_type?: string; page?: number; limit?: number }) =>
    // 0b-02c: backend wraps in canonical envelope {success, data: {data, total}}; flatten to preserve old caller contract.
    api.get('/activities', { params: filters }).then(r => ({ ...r, data: { success: r.data.success, ...r.data.data } })),
  create: (data: Record<string, any>) => api.post('/activities', data),
};

// ── Assignment Rules API ────────────────────────────────────────────

// ── Engineering (Feature Requests & Bug Reports) ────────────────────
export interface EngineeringRequest {
  id: number;
  title: string;
  description: string;
  request_type: 'bug' | 'feature';
  status: string;
  priority: string;
  severity: string | null;
  submitter_id: number | null;
  submitter_name: string | null;
  submitter_email: string | null;
  page_url: string | null;
  steps_to_reproduce: string | null;
  expected_behavior: string | null;
  actual_behavior: string | null;
  user_agent: string | null;
  notes: string | null;
  resolution: string | null;
  created_at: string;
  updated_at: string;
  attachments?: any[];
  history?: any[];
}

export const engineeringApi = {
  listRequests: (params?: Record<string, any>): Promise<{ success: boolean; data: EngineeringRequest[]; total: number }> =>
    api.get('/engineering/requests', { params }).then(r => r.data),
  getRequest: (id: number): Promise<{ success: boolean; data: EngineeringRequest }> =>
    api.get(`/engineering/requests/${id}`).then(r => r.data),
  createRequest: (formData: FormData): Promise<{ success: boolean; data: EngineeringRequest }> =>
    api.post('/engineering/requests', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  updateRequest: (id: number, data: Partial<EngineeringRequest>): Promise<{ success: boolean; data: EngineeringRequest }> =>
    api.patch(`/engineering/requests/${id}`, data).then(r => r.data),
  exportJson: (includeAll = false): Promise<any> =>
    api.get(`/engineering/requests/export${includeAll ? '?all=1' : ''}`).then(r => r.data.data),
  batchUpdateStatus: (ids: number[], status: string, notesAppend?: string, resolution?: string): Promise<{ success: boolean; updated: number }> =>
    // 0b-02c: backend nests {updated, status} under .data; unpack to preserve flat shape.
    api.patch('/engineering/requests/batch-status', { ids, status, notes_append: notesAppend, resolution }).then(r => ({ success: r.data.success, ...r.data.data })),
};

// ── CSAT ──────────────────────────────────────────────────────────────
export const csatApi = {
  sendTest: (email: string): Promise<{ success: boolean; data: { token: string } }> =>
    api.post('/csat/test', { email }).then(r => r.data),
  getTicketMetrics: (params?: { days?: number; assignee?: number; client_card_id?: string }): Promise<{ success: boolean; data: any }> =>
    api.get('/csat/metrics/tickets', { params }).then(r => r.data),
  getInboxMetrics: (params?: { days?: number }): Promise<{ success: boolean; data: any }> =>
    api.get('/csat/metrics/inbox', { params }).then(r => r.data),
};

// ── Wave W Chunk C+ — CSAT reviews (per-client, per-user) ──────────────
// Backs the Reviews tab on ClientDetailView, the ClientsList per-row
// unread indicator, and the notification deep-link target. All routes
// scoped to the current user via session auth.
export interface ReviewRow {
  id: number;
  ticket_id: number | null;
  ticket_title: string | null;
  client_card_id: string | null;
  contact_email: string;
  submitter_contact_name: string | null;
  assigned_user_id_at_send: number | null;
  assignee_at_send_name: string | null;
  rating: number;
  comment: string | null;
  has_comment: number;
  sent_at: string;
  responded_at: string;
  is_read: number;
  thread_id?: number | null;
}
export const reviewsApi = {
  listForClient: (cardId: string): Promise<{ success: boolean; data: ReviewRow[] }> =>
    api.get(`/reviews/clients/${encodeURIComponent(cardId)}`).then(r => r.data),
  summary: (cardId: string): Promise<{ success: boolean; data: { count: number; average: number } | null }> =>
    api.get(`/reviews/clients/${encodeURIComponent(cardId)}/summary`).then(r => r.data),
  unreadByClient: (): Promise<{ success: boolean; data: Record<string, number> }> =>
    api.get('/reviews/unread-by-client').then(r => r.data),
  markRead: (reviewId: number): Promise<{ success: boolean; data: { marked: boolean } }> =>
    api.post(`/reviews/${reviewId}/mark-read`).then(r => r.data),
};

// ── Wave W Chunk C+ — Notification preferences (per-user opt-in/out) ───
// Generic per-type toggle. First consumer: 'csat_review_received'.
export interface NotificationPref {
  notification_type: string;
  enabled: boolean;
  updated_at: string | null;
}
export const notificationPreferencesApi = {
  list: (): Promise<{ success: boolean; data: NotificationPref[] }> =>
    api.get('/users/me/notification-preferences').then(r => r.data),
  update: (type: string, enabled: boolean): Promise<{ success: boolean; data: NotificationPref }> =>
    api.patch(`/users/me/notification-preferences/${encodeURIComponent(type)}`, { enabled }).then(r => r.data),
};

// ── Changelog / What's New ─────────────────────────────────────────────
export const changelogApi = {
  getEntries: (params?: { type?: string; category?: string; limit?: number; offset?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/changelog', { params }).then(r => r.data),
  getUnreadCount: (): Promise<{ success: boolean; data: { count: number } }> =>
    api.get('/changelog/unread-count').then(r => r.data),
  createEntry: (data: { title: string; body_html?: string; entry_type: string; category: string; version_tag?: string; published_at?: string | null }): Promise<{ success: boolean; data: any }> =>
    api.post('/changelog', data).then(r => r.data),
  updateEntry: (id: number, data: any): Promise<{ success: boolean; data: any }> =>
    api.put(`/changelog/${id}`, data).then(r => r.data),
  deleteEntry: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/changelog/${id}`).then(r => r.data),
  uploadImage: (entryId: number, file: File, caption?: string): Promise<{ success: boolean; data: any }> => {
    const form = new FormData();
    form.append('file', file);
    if (caption) form.append('caption', caption);
    return api.post(`/changelog/${entryId}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteImage: (imageId: number): Promise<{ success: boolean }> =>
    api.delete(`/changelog/images/${imageId}`).then(r => r.data),
  imageUrl: (filename: string): string => `${api.defaults.baseURL}/changelog/images/file/${filename}`,
  markAsRead: (entryId: number): Promise<{ success: boolean }> =>
    api.post(`/changelog/${entryId}/read`).then(r => r.data),
  markAllAsRead: (): Promise<{ success: boolean }> =>
    api.post('/changelog/mark-all-read').then(r => r.data),
};

// ── Template Library ────────────────────────────────────────────────
export const templatesApi = {
  getFolders: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/templates/folders').then(r => r.data),
  createFolder: (data: { name: string; parent_folder_id?: number | null; color?: string }): Promise<{ success: boolean; data: any }> =>
    api.post('/templates/folders', data).then(r => r.data),
  updateFolder: (id: number, data: { name?: string; color?: string; parent_folder_id?: number | null; sort_order?: number }): Promise<{ success: boolean }> =>
    api.put(`/templates/folders/${id}`, data).then(r => r.data),
  deleteFolder: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/templates/folders/${id}`).then(r => r.data),
  getTemplates: (params?: { folder_id?: number | null; template_type?: string; is_library?: boolean; process_template_id?: number | null; search?: string; limit?: number; offset?: number }): Promise<{ success: boolean; data: any[]; total: number }> =>
    api.get('/templates', { params }).then(r => r.data),
  getTemplate: (id: number): Promise<{ success: boolean; data: any }> =>
    api.get(`/templates/${id}`).then(r => r.data),
  createTemplate: (data: { name: string; folder_id?: number | null; subject?: string; body_html?: string; body_json?: string; template_type?: string; is_library?: boolean; process_template_id?: number | null }): Promise<{ success: boolean; data: any }> =>
    api.post('/templates', data).then(r => r.data),
  updateTemplate: (id: number, data: Record<string, any>): Promise<{ success: boolean; data: any }> =>
    api.put(`/templates/${id}`, data).then(r => r.data),
  deleteTemplate: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/templates/${id}`).then(r => r.data),
  duplicateTemplate: (id: number): Promise<{ success: boolean; data: any }> =>
    api.post(`/templates/${id}/duplicate`).then(r => r.data),
  getMergeFields: (context?: string): Promise<{ success: boolean; data: any[] }> =>
    api.get('/templates/merge-fields', { params: { context } }).then(r => r.data),
  getTemplateUsageBulk: (templateIds: number[]): Promise<{ success: boolean; data: Record<number, Array<{ type: string; id: number; name: string }>> }> =>
    api.post('/templates/usage/bulk', { template_ids: templateIds }).then(r => r.data),
  getAttachments: (templateId: number): Promise<{ success: boolean; data: any[] }> =>
    api.get(`/templates/${templateId}/attachments`).then(r => r.data),
  addAttachment: (templateId: number, data: { filename: string; mime_type: string; size_bytes: number; data_base64: string }): Promise<{ success: boolean; data: any }> =>
    api.post(`/templates/${templateId}/attachments`, data).then(r => r.data),
  deleteAttachment: (attachmentId: number): Promise<{ success: boolean }> =>
    api.delete(`/templates/attachments/${attachmentId}`).then(r => r.data),
  aiDraftContent: (data: { prompt: string; tone?: string; length?: string; context?: string }): Promise<{ success: boolean; data: { content: string } }> =>
    api.post('/templates/ai/draft', data).then(r => r.data),
  aiSubjectLines: (data: { body_preview: string; tone?: string; count?: number }): Promise<{ success: boolean; data: string[] }> =>
    api.post('/templates/ai/subject-line', data).then(r => r.data),
  getEmailAssets: (opts?: { search?: string; limit?: number; offset?: number; category?: 'gallery' | 'brand' }): Promise<{ success: boolean; data: any[]; total: number }> =>
    // 0b-02c: backend nests {data, total} under .data; unpack to preserve flat shape.
    api.get('/templates/assets', { params: opts }).then(r => ({ success: r.data.success, ...r.data.data })),
  uploadEmailAsset: (data: { data_base64: string; filename: string; mime_type?: string; alt_text?: string; tags?: string; category?: 'gallery' | 'brand' }): Promise<{ success: boolean; data: any }> =>
    api.post('/templates/assets/upload', data).then(r => r.data),
  deleteEmailAsset: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/templates/assets/${id}`).then(r => r.data),
  getTemplateTags: (): Promise<{ success: boolean; data: Array<{ id: number; name: string; color: string }> }> =>
    api.get('/templates/tags/all').then(r => r.data),
  createTemplateTag: (data: { name: string; color?: string }): Promise<{ success: boolean; data: any }> =>
    api.post('/templates/tags', data).then(r => r.data),
  getTemplateTagsForTemplate: (templateId: number): Promise<{ success: boolean; data: Array<{ id: number; name: string; color: string }> }> =>
    api.get(`/templates/${templateId}/tags`).then(r => r.data),
  setTemplateTagsForTemplate: (templateId: number, tagIds: number[]): Promise<{ success: boolean }> =>
    api.put(`/templates/${templateId}/tags`, { tag_ids: tagIds }).then(r => r.data),
  getBrandKits: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/templates/brand-kits/all').then(r => r.data),
  createBrandKit: (data: Record<string, any>): Promise<{ success: boolean; data: any }> =>
    api.post('/templates/brand-kits', data).then(r => r.data),
  updateBrandKit: (id: number, data: Record<string, any>): Promise<{ success: boolean }> =>
    api.put(`/templates/brand-kits/${id}`, data).then(r => r.data),
  deleteBrandKit: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/templates/brand-kits/${id}`).then(r => r.data),
  testSend: (data: { to: string; subject: string; html?: string; body_html?: string; template_type?: string; blocks?: any[]; settings?: Record<string, any> }): Promise<{ success: boolean }> =>
    api.post('/templates/test-send', data).then(r => r.data),
  accessibilityCheck: (html: string): Promise<{ success: boolean; data: Array<{ severity: string; message: string }> }> =>
    api.post('/templates/accessibility-check', { html }).then(r => r.data),
};

// ── Campaign Tracking / Analytics ───────────────────────────────────
export const trackingApi = {
  getCampaignAnalytics: (campaignId: number): Promise<{ success: boolean; data: any }> =>
    api.get(`/track/analytics/${campaignId}`).then(r => r.data),
};

// ── Dashboard Notes ────────────────────────────────────────────────
export const notesApi = {
  getNotes: (): Promise<{ success: boolean; data: any[] }> =>
    api.get('/notes').then(r => r.data),
  createNote: (data: { title?: string; content?: string; color?: string }): Promise<{ success: boolean; data: any }> =>
    api.post('/notes', data).then(r => r.data),
  updateNote: (id: number, data: Record<string, any>): Promise<{ success: boolean; data: any }> =>
    api.put(`/notes/${id}`, data).then(r => r.data),
  deleteNote: (id: number): Promise<{ success: boolean }> =>
    api.delete(`/notes/${id}`).then(r => r.data),
  convertNote: (id: number, type: 'todo' | 'ticket' | 'project'): Promise<{ success: boolean; data: { type: string; id: number } }> =>
    api.post(`/notes/${id}/convert`, { type }).then(r => r.data),
};

// ── CRM Campaigns (enrollment / marketing) ───────────────────────────
// 0b-02c: list/getContacts/getContactCampaigns/getEventsByType return multi-field
// {data, total} — unpack to preserve flat caller contract. Other endpoints
// return single-data, so .data.data unwraps cleanly.
export const campaignsApi = {
  list: (filters?: { status?: string; type?: string; page?: number; limit?: number }) =>
    api.get('/campaigns', { params: filters }).then(r => ({ success: r.data.success, ...r.data.data })),
  get: (id: number) => api.get(`/campaigns/${id}`).then(r => r.data.data),
  create: (data: { name: string; description?: string; type?: string; workflow_template_id?: number; status?: string }) =>
    api.post('/campaigns', data).then(r => r.data.data),
  update: (id: number, data: Record<string, any>) => api.put(`/campaigns/${id}`, data).then(r => r.data.data),
  remove: (id: number) => api.delete(`/campaigns/${id}`).then(r => r.data.data),
  getContacts: (campaignId: number, filters?: { status?: string; search?: string; page?: number; limit?: number }) =>
    api.get(`/campaigns/${campaignId}/contacts`, { params: filters }).then(r => ({ success: r.data.success, ...r.data.data })),
  enrollContacts: (campaignId: number, contactIds: number[]) =>
    api.post(`/campaigns/${campaignId}/enroll`, { contact_ids: contactIds }).then(r => r.data.data),
  enrollByTags: (campaignId: number, tagIds: number[], mode: 'any' | 'all') =>
    api.post(`/campaigns/${campaignId}/enroll-by-tags`, { tag_ids: tagIds, mode }).then(r => r.data.data),
  unenrollContact: (campaignId: number, contactId: number) =>
    api.delete(`/campaigns/${campaignId}/contacts/${contactId}`).then(r => r.data.data),
  resubscribeContact: (campaignId: number, contactId: number) =>
    api.post(`/campaigns/${campaignId}/contacts/${contactId}/resubscribe`).then(r => r.data.data),
  getStats: (campaignId: number) => api.get(`/campaigns/${campaignId}/stats`).then(r => r.data.data),
  getAvailableSenders: () => api.get('/campaigns/available-senders').then(r => r.data.data),
  getContactEvents: (campaignId: number, contactId: number) =>
    api.get(`/campaigns/${campaignId}/contacts/${contactId}/events`).then(r => r.data.data),
  getContactProgress: (campaignId: number, contactId: number): Promise<{ success: boolean; data: any }> =>
    api.get(`/campaigns/${campaignId}/contacts/${contactId}/progress`).then(r => r.data),
};

// ── Contact tags ─────────────────────────────────────────────────────
export const contactTagsApi = {
  listTags: () => api.get('/contact-tags').then(r => r.data),
  createTag: (data: { name: string; color?: string }) => api.post('/contact-tags', data).then(r => r.data),
  searchTags: (query: string) => api.get('/contact-tags/search', { params: { q: query } }).then(r => r.data),
  assignTags: (contactId: number, tagIds: number[]) =>
    api.post(`/contact-tags/contacts/${contactId}/tags`, { tag_ids: tagIds }).then(r => r.data),
  removeTag: (contactId: number, tagId: number) =>
    api.delete(`/contact-tags/contacts/${contactId}/tags/${tagId}`).then(r => r.data),
  getContactsByTags: (tagIds: number[], mode: 'any' | 'all', filters?: { search?: string; page?: number; limit?: number }) =>
    // 0b-02c: backend nests {data, total} under .data; unpack to preserve flat shape.
    api.get('/contact-tags/by-tags', { params: { tag_ids: tagIds.join(','), mode, ...filters } }).then(r => ({ success: r.data.success, ...r.data.data })),
};

// ── Scheduling ──────────────────────────────────────────────────────
export const schedulingApi = {
  listEventTypes: (status?: string) =>
    api.get('/scheduling/event-types', { params: status ? { status } : {} }).then(r => r.data),
  createEventType: (data: { name: string; slug?: string; mode?: string; duration_minutes?: number; description?: string; location_type?: string; timezone?: string; google_meet_enabled?: boolean }) =>
    api.post('/scheduling/event-types', data).then(r => r.data),
  getEventType: (id: number) =>
    api.get(`/scheduling/event-types/${id}`).then(r => r.data),
  updateEventType: (id: number, data: Record<string, any>) =>
    api.patch(`/scheduling/event-types/${id}`, data).then(r => r.data),
  deleteEventType: (id: number) =>
    api.delete(`/scheduling/event-types/${id}`).then(r => r.data),
  // Port B preview polish: restore an archived event type → draft.
  restoreEventType: (id: number) =>
    api.post(`/scheduling/event-types/${id}/restore`).then(r => r.data),
  // Port B preview polish: hard-delete an archived event type. Only
  // valid for status='archived' (backend returns 400 otherwise).
  hardDeleteEventType: (id: number) =>
    api.delete(`/scheduling/event-types/${id}/permanent`).then(r => r.data),
  publishEventType: (id: number) =>
    api.post(`/scheduling/event-types/${id}/publish`).then(r => r.data),
  duplicateEventType: (id: number) =>
    api.post(`/scheduling/event-types/${id}/duplicate`).then(r => r.data),
  setHosts: (eventTypeId: number, hosts: Array<{ user_id: number; role?: string; sort_order?: number; write_calendar_id?: string; conflict_calendar_ids_json?: string }>) =>
    api.put(`/scheduling/event-types/${eventTypeId}/hosts`, { hosts }).then(r => r.data),
  setAvailabilityRules: (eventTypeId: number, rules: Array<{ day_of_week: number; start_time: string; end_time: string }>) =>
    api.put(`/scheduling/event-types/${eventTypeId}/availability`, { rules }).then(r => r.data),
  setOverrides: (eventTypeId: number, overrides: Array<{ override_date: string; start_time?: string; end_time?: string; is_blocked?: boolean; label?: string }>) =>
    api.put(`/scheduling/event-types/${eventTypeId}/overrides`, { overrides }).then(r => r.data),
  setQuestions: (eventTypeId: number, questions: Array<{ label: string; field_key: string; field_type?: string; is_required?: boolean; placeholder?: string; help_text?: string; options?: string[]; property_id?: number; sort_order?: number }>) =>
    api.put(`/scheduling/event-types/${eventTypeId}/questions`, { questions }).then(r => r.data),
  listBookings: (params?: { event_type_id?: number; status?: string; limit?: number; offset?: number }) =>
    api.get('/scheduling/bookings', { params }).then(r => r.data.data),
  markNoShow: (id: number) =>
    api.post(`/scheduling/bookings/${id}/mark-no-show`).then(r => r.data),
  listCalendars: () =>
    api.get('/scheduling/calendars').then(r => r.data),
  // Chunk 4 (state.md Entry 72): canvas contact-booked trigger token surface.
  // getBookingTokenDefs → available token defs for an event type (metadata +
  // one per booking question, typed) for the Create Deal picker + trigger preview.
  getBookingTokenDefs: (eventTypeId: number) =>
    api.get(`/scheduling/event-types/${eventTypeId}/booking-token-defs`).then(r => r.data.data),
  // getBookingTokens → a recent booking's tokens with resolved values (sample fire).
  getBookingTokens: (bookingId: number) =>
    api.get(`/scheduling/bookings/${bookingId}/tokens`).then(r => r.data.data),
};

// ── Knowledge Base ──────────────────────────────────────────────────────

export const knowledgeApi = {
  listDocuments: (params?: Record<string, any>) =>
    // 0b-02c: backend nests {documents, total} under .data; unpack to preserve flat shape.
    api.get('/knowledge/documents', { params }).then(r => ({ success: r.data.success, ...r.data.data })),
  getDocument: (id: number) =>
    api.get(`/knowledge/documents/${id}`).then(r => r.data),
  createDocument: (data: Record<string, any>) =>
    api.post('/knowledge/documents', data).then(r => r.data),
  updateDocument: (id: number, data: Record<string, any>) =>
    api.put(`/knowledge/documents/${id}`, data).then(r => r.data),
  deleteDocument: (id: number) =>
    api.delete(`/knowledge/documents/${id}`).then(r => r.data),
  publishDocument: (id: number) =>
    api.post(`/knowledge/documents/${id}/publish`).then(r => r.data),
  unpublishDocument: (id: number) =>
    api.post(`/knowledge/documents/${id}/unpublish`).then(r => r.data),
  getTags: () =>
    api.get('/knowledge/tags').then(r => r.data),
  createTag: (data: { name: string; color?: string }) =>
    api.post('/knowledge/tags', data).then(r => r.data),
  deleteTag: (id: number) =>
    api.delete(`/knowledge/tags/${id}`).then(r => r.data),
};

export default api;
